/**
 * The schedule: a plan you can actually edit.
 *
 * A Gantt chart that only draws is a report. What makes one a planning tool is that
 * dragging a bar is a decision — the thing it was blocking moves too, the finish date
 * changes, and you can see straight away whether that matters. So this is a grid and a
 * chart side by side, sharing rows: type in the grid, drag on the chart, and both are
 * the same plan.
 *
 * Three things it does that a read-only chart cannot.
 *
 * **A drag says what else it moved.** Pushing a task pushes everything waiting on it,
 * and the toast names them. A change that quietly rescheduled four other people's work
 * is one somebody finds out about in a status meeting.
 *
 * **Undo is one act.** A drag and its cascade go back together, because they happened
 * together. Undoing half a cascade would leave a plan that satisfies none of its own
 * dependencies.
 *
 * **Duration is in working days.** Five days from a Thursday ends the following
 * Wednesday. Everything else here is arithmetic; this is the bit that makes the dates
 * match what people actually mean.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '../../lib/router'
import clsx from 'clsx'
import { useUserOptions } from '../../lib/queries'
import { shortDate } from '../../lib/format'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import {
  Button,
  Dialog,
  EmptyState,
  ErrorNote,
  Picker,
  Spinner,
  useToast,
} from '../../components/ui'
import { PageHeader } from '../../components/DataTable'
import { Divider, PlanToolbar, Tip, Tool } from './Toolbar'
import {
  type Column,
  BASE_DAY,
  ZOOM_STEPS,
  chosen,
  clampSplit,
  dayWidthAt,
  defaultSplit,
  DENSITIES,
  matchesFilter,
  useColumnChoice,
  useColumnWidths,
  useDensity,
  useSplit,
  useZoom,
} from './columns'
import { ColumnChooser, columnMenuEntries } from './ColumnChooser'
import { MarksLegend, MilestoneDiamond, SummaryBracket, TaskFill, phaseColours } from './marks'
import { DayHoursProvider, dayHours, formatLength, lengthHint, parseLength, useDayHours } from './length'

/** Re-exported: the mind map and its tests have always taken it from here. */
export { phaseColours }
import { ContextMenu, type MenuEntry } from './ContextMenu'
import {
  AddIcon,
  ChevronDownIcon,
  CriticalIcon,
  DensityIcon,
  FilterIcon,
  FitIcon,
  IndentIcon,
  InsertAboveIcon,
  LinkIcon,
  OpenIcon,
  OutdentIcon,
  RedoIcon,
  TickIcon,
  TodayIcon,
  TrashIcon,
  UnlinkIcon,
  DeckIcon,
  ExportIcon,
  ImportIcon,
  PrintIcon,
  TightenIcon,
  UndoIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from '../../components/icons'
import { Menu, MenuItem } from '../../components/overlay'
import { PredecessorCell } from './Predecessors'
import { anchors, route } from './links'
import { RuleMarks } from './RuleMarks'
import {
  SLOP,
  daysMoved,
  dropGap,
  landing,
  resultOf,
  type Drag,
  type Grab,
} from './dragging'
import {
  planRows,
  selectOnFocus,
  usePlan,
  usePlanEdits,
  useUndo,
  type Dependency,
  type Moved,
  type Row,
  type UndoAction,
  type UndoStep,
  placeOf,
  previousOf,
  redoStepFor, useShowClosed } from './plan'

/* ----------------------------------------------------------------------- geometry */

/**
 * Row and bar heights. Module-level rather than props because two dozen places in this
 * file lay things out from them, and a person switching to compact rows re-renders the
 * whole screen anyway. Set once, at the top of GanttPage, before anything draws.
 */
let ROW_H: number = DENSITIES.comfortable
let BAR_H = 18
/** The divider between the grid and the chart. */
const SPLITTER_W = 6
/** The + at the end of the grid's header that opens the column menu. */
const COLUMN_ADD_W = 28
/** The row-number column on the far left of the grid, the way every spreadsheet has one. */
const NUMBER_W = 34

/**
 * The colour behind a row, stated rather than layered.
 *
 * The pinned columns sit over the rest of the grid as it scrolls, so they need the same
 * paint as the row they belong to — a transparent sticky cell shows the scrolling cells
 * sliding underneath it.
 */
function rowBackground(
  index: number,
  key: string,
  selected: string | null,
  picked: Set<string>,
  hovered: string | null,
): string {
  if (picked.has(key) || selected === key) return 'var(--color-accent-soft)'
  if (hovered === key) return 'color-mix(in srgb, var(--color-ink) 4%, var(--color-surface))'
  return index % 2 === 1 ? 'var(--gantt-zebra)' : 'var(--color-surface)'
}
const TODAY_LANE = 14
const LABEL_LANE = 32

const DAY = 86_400_000
const parse = (iso: string) => new Date(`${iso}T00:00:00`)
const daysBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / DAY)
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * DAY)

/** Saturday or Sunday. Shaded on the chart so a bar's length is readable at a glance. */
const isWeekend = (d: Date) => d.getDay() === 0 || d.getDay() === 6

function barColour(row: Row, showCritical: boolean, phase: string): string {
  if (showCritical && row.critical && !row.has_children) return 'var(--gantt-critical)'
  if (row.late && !row.has_children) return 'var(--gantt-critical)'
  return phase
}

/* ------------------------------------------------------------------- the component */

/** Which phases are folded shut, remembered per plan. */
function useFolded(scope: string): [Set<string>, (f: (prev: Set<string>) => Set<string>) => void] {
  const store = `furi.plan.folded.${scope}`
  const [folded, setFoldedState] = useState<Set<string>>(new Set())
  useEffect(() => {
    try {
      const saved = localStorage.getItem(store)
      const parsed: unknown = saved ? JSON.parse(saved) : null
      setFoldedState(new Set(Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : []))
    } catch {
      setFoldedState(new Set())
    }
  }, [store])
  const setFolded = useCallback(
    (f: (prev: Set<string>) => Set<string>) => {
      setFoldedState((prev) => {
        const next = f(prev)
        try {
          localStorage.setItem(store, JSON.stringify([...next]))
        } catch {
          // A browser refusing to remember is not a reason to refuse the fold.
        }
        return next
      })
    },
    [store],
  )
  return [folded, setFolded]
}

/**
 * Everything about a task, on hover.
 *
 * The bar says when; this says the rest — who, how far, how much room it has, what rule
 * it is under — without a click, because the question "what is this one" is asked more
 * often than anything else on a chart and should cost nothing.
 */
function Peek({ row, at }: { row: Row; at: { x: number; y: number } }) {
  const perDay = useDayHours()
  const slack = row.free_slack_days ?? row.slack_days
  return (
    <div
      role="tooltip"
      className="pointer-events-none absolute z-[6] w-64 rounded-[6px] border border-[var(--color-rule)] bg-[var(--color-surface)] p-2.5 text-[11px] shadow-xl"
      style={{ left: Math.max(0, at.x - 24), top: at.y + ROW_H + 2 }}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-[10px] text-[var(--color-ink-faint)]">{row.key}</span>
        {row.status_name && <span className="text-[10px] text-[var(--color-ink-dim)]">{row.status_name}</span>}
      </div>
      <div className="mt-0.5 text-[12px] font-medium leading-snug">{row.title}</div>
      <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[var(--color-ink-dim)]">
        <dt>When</dt>
        <dd className="text-[var(--color-ink)]">
          {row.start && shortDate(row.start)}
          {row.end && row.end !== row.start && ` – ${shortDate(row.end)}`}
          {row.working_days ? ` · ${formatLength(row.working_days, perDay)}` : ''}
        </dd>
        {row.assignee && (<><dt>Who</dt><dd className="text-[var(--color-ink)]">{row.assignee}</dd></>)}
        {row.progress_pct !== null && row.progress_pct !== undefined && (
          <><dt>Done</dt><dd className="text-[var(--color-ink)]">{Math.round(row.progress_pct)}%</dd></>
        )}
        {slack !== null && slack !== undefined && !row.has_children && row.status_category !== 'done' && (
          <><dt>Slack</dt><dd className={clsx(slack <= 0 ? 'text-[var(--gantt-critical)]' : 'text-[var(--color-ink)]')}>{slack <= 0 ? 'none — on the critical path' : `${slack} working days`}</dd></>
        )}
        {row.constraint_type !== 'asap' && (
          <><dt>Rule</dt><dd className="text-[var(--color-ink)]">{row.constraint_label}{row.constraint_on ? ` ${shortDate(row.constraint_on)}` : ''}</dd></>
        )}
        {row.deadline_on && (
          <><dt>Deadline</dt><dd className={clsx((row.past_deadline_by ?? 0) > 0 ? 'text-[var(--color-danger)]' : 'text-[var(--color-ink)]')}>{shortDate(row.deadline_on)}{(row.past_deadline_by ?? 0) > 0 ? ` · ${row.past_deadline_by}d late` : ''}</dd></>
        )}
        {row.baseline_start && row.start && row.baseline_end && row.end && (row.baseline_start !== row.start || row.baseline_end !== row.end) && (
          <><dt>Agreed</dt><dd className="text-[var(--color-ink)]">{shortDate(row.baseline_start)} – {shortDate(row.baseline_end)}</dd></>
        )}
        {(row.predecessors ?? []).length > 0 && (
          <><dt>Waits for</dt><dd className="font-mono text-[10px] text-[var(--color-ink)]">{(row.predecessors ?? []).map((p) => p.key).join(', ')}</dd></>
        )}
      </dl>
    </div>
  )
}


export function GanttPage({
  scopeTo,
  onOpenTask,
}: {
  scopeTo: string
  /**
   * Opens a task. Supplied by whatever is hosting the chart, because only the host knows how
   * — inside the board it is the drawer, and the URL it lives in carries the tab you are on.
   *
   * Both call sites used to `navigate({ to: '/work/$key' })`, a route from the kit this was
   * ported from that does not exist here. React Router matched the catch-all instead, so
   * clicking a bar took you to the landing page — which reads exactly like "the task does not
   * open".
   */
  onOpenTask?: (taskId: string) => void
}) {
  const navigate = useNavigate()
  const users = useUserOptions(scopeTo)

  const zoom = useZoom('gantt')
  const split = useSplit('gantt')
  //: A drag on the divider between the grid and the chart, while it is going on.
  const splitDrag = useRef<{ fromX: number; from: number } | null>(null)
  //: A drag on a column's edge, while it is going on.
  const colDrag = useRef<{ key: string; fromX: number; from: number } | null>(null)
  const [showCritical, setShowCritical] = useState(false)
  const density = useDensity('gantt')
  ROW_H = density.rowHeight
  BAR_H = ROW_H >= 30 ? 18 : 14
  //: The row most recently clicked — the anchor for shift-click ranges and the row the
  //: single-task tools act on.
  const [selected, setSelected] = useState<string | null>(null)
  //: Every row that is selected. One, usually; several with ctrl-click or shift-click,
  //: which is how two tasks get linked or five get indented at once.
  const [picked, setPicked] = useState<Set<string>>(new Set())
  //: The right-click menu, if one is open: which row, and where.
  const [menu, setMenu] = useState<{ key: string; x: number; y: number } | null>(null)
  //: The column menu — a right-click on the grid's header, or the + at its end — and,
  //: when it was a particular column that was clicked, which one.
  const [columnMenu, setColumnMenu] = useState<{ x: number; y: number; key?: string } | null>(null)
  //: A quick filter over the grid. Ancestors of a match stay, so the outline still reads.
  const [filter, setFilter] = useState('')
  const [redoStack, setRedoStack] = useState<UndoStep[]>([])
  const [editing, setEditing] = useState<{ key: string; field: string } | null>(null)
  const [drag, setDrag] = useState<Drag | null>(null)
  //: Which gap a row being dragged up or down the grid is currently over.
  const [rowDrag, setRowDrag] = useState<{ key: string; gap: number } | null>(null)
  //: Which task's scheduling rules are open, if any.
  const [error, setError] = useState<Error | null>(null)
  const [history, setHistory] = useState<UndoStep[]>([])
  //: Phases folded shut, remembered per plan so a long outline opens the way it was left.
  const [folded, setFolded] = useFolded(scopeTo)
  //: The row under the pointer, so the grid and the chart light up together.
  const [hovered, setHovered] = useState<string | null>(null)
  //: The bar being hovered, for the card that says everything about it.
  const [peek, setPeek] = useState<{ key: string; x: number; y: number } | null>(null)
  const gridPaneRef = useRef<HTMLDivElement>(null)
  const chartPaneRef = useRef<HTMLDivElement>(null)
  //: How tall the two panes are: the room from the top of the card to the foot of the
  //: window, measured rather than guessed, so the scrollbars sit at the bottom of the
  //: screen and stay there.
  const cardRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<HTMLDivElement>(null)
  const frameRef = useRef<HTMLDivElement>(null)
  //: The two panes together, so the frame can measure what else the card holds.
  const panesRef = useRef<HTMLDivElement>(null)
  const [available, setAvailable] = useState(1200)
  const choice = useColumnChoice('gantt')

  const [showClosed, setShowClosed] = useShowClosed(scopeTo)
  const plan = usePlan(scopeTo, showClosed)
  const edits = usePlanEdits(scopeTo, setError)
  const undo = useUndo(scopeTo, () => setHistory((h) => h.slice(0, -1)))
  const redo = useUndo(scopeTo, () => setRedoStack((r) => r.slice(0, -1)))

  /**
   * WHETHER THIS PROJECT CAN BE PLANNED AT ALL, read from the payload rather than from the kit's
   * permission hook — which answered from every company the person belonged to at once, so being
   * a developer for one customer made another customer's plan editable, and a plain client
   * (`work.write: own`) made every plan editable.
   *
   * What may be done to a row that already exists is per row: `editable`, `may_move`,
   * `may_delete`, each answered by the API from the same rule the board uses.
   */
  const writable = (plan.data?.capabilities['work.write'] ?? 'none') !== 'none'
  /** `work.delete` — ours, and neither client role's. See the capability. */
  const mayDelete = (plan.data?.capabilities['work.delete'] ?? 'none') !== 'none'
  /**
   * WHETHER THIS PLAN'S SHAPE IS THEIRS TO CHANGE — asked by SIDE, not by capability.
   *
   * Adding work, arranging what is already on it, and saying what waits for what are one
   * question. Their lead holds `work.write` at full, so `writable` says yes to them, and this
   * screen was a way to put work into our schedule and re-parent the rest of it, with none of
   * the questions "Report it" asks — while the same person could not set a due date, which made
   * the permission stranger than either half of it sounds.
   *
   * Editing the WORK — its wording, its detail, how badly it hurts, who on their side is on it —
   * is a different question and is answered per row.
   */
  const mayPlan = writable && !plan.data?.customer
  const dayWidth = dayWidthAt(zoom.percent)

  /** One edit, remembered as the requests that put it back. Fifty deep. A new edit
   *  empties the redo stack: what was undone can no longer be redone over the top of
   *  something else, which is the rule every editor since Emacs has followed. */
  const record = useCallback((step: UndoStep) => {
    if (step.length) {
      setHistory((h) => [...h.slice(-49), step])
      setRedoStack([])
    }
  }, [])
  /** The shorthand for an edit that only moved things. */
  const remember = useCallback(
    (moved: Moved[]) => record(moved.length ? [{ kind: 'moves', moved }] : []),
    [record],
  )

  const move = useCallback(
    (input: { key: string; start?: string; end?: string; days?: number }) =>
      edits.move.mutateAsync(input).then((r) => remember(r.moved)),
    [edits.move, remember],
  )

  const data = plan.data
  const all = useMemo(() => planRows(data), [data])

  /**
   * Ticking a row off. It briefly asked what fixed it, because the API refused without a note;
   * that demand has gone — see `TestPanel` for why it asked the wrong person.
   */
  const tickOff = useCallback(
    async (key: string, done: boolean) => {
      await edits.tick.mutateAsync({ key, done })
      record([{ kind: 'tick', key, done: !done }])
    },
    [edits.tick, record],
  )

  /** The chart works in keys; the task drawer is addressed by id. */
  const openByKey = (key: string) => {
    const row = all.find((r) => r.key === key)
    if (row && onOpenTask) onOpenTask(row.id)
  }

  /** Drawing a link on the chart: undone by removing it, and un-moving what it moved. */
  const link = useCallback(
    (input: { from: string; to: string }) =>
      edits.link.mutateAsync(input).then((r) =>
        record([
          { kind: 'unlink', from: input.from, to: input.to },
          ...(r.moved.length ? [{ kind: 'moves', moved: r.moved } as UndoAction] : []),
        ]),
      ),
    [edits.link, record],
  )

  /** A cell edit: undone by writing the old value back, and un-moving what it moved. */
  const commit = useCallback(
    (key: string, body: Record<string, unknown>) => {
      const row = all.find((r) => r.key === key)
      const before = row ? previousOf(row, body) : {}
      return edits.amend.mutateAsync({ key, body }).then((r) =>
        record([
          ...(Object.keys(before).length ? [{ kind: 'patch', key, body: before } as UndoAction] : []),
          ...(r.moved.length ? [{ kind: 'moves', moved: r.moved } as UndoAction] : []),
        ]),
      )
    },
    [all, edits.amend, record],
  )

  /** Indent, outdent, up, down: undone by putting the row back exactly where it was. */
  const restructure = useCallback(
    (key: string, act: () => Promise<unknown>) => {
      const row = all.find((r) => r.key === key)
      const was = row ? placeOf(row, all) : null
      return act().then(() => {
        if (was) record([{ kind: 'place', key, after: was.after, parent: was.parent }])
      })
    },
    [all, record],
  )

  /** The rows on screen: everything, less what sits under a folded phase. */
  const visible = useMemo(() => {
    const byId = new Map(all.map((r) => [r.id, r]))
    const hidden = (row: Row): boolean => {
      let cursor = row.parent_id ? byId.get(row.parent_id) : undefined
      let hops = 0
      while (cursor && hops < 50) {
        if (folded.has(cursor.key)) return true
        cursor = cursor.parent_id ? byId.get(cursor.parent_id) : undefined
        hops += 1
      }
      return false
    }
    const unfolded = all.filter((r) => !hidden(r))
    if (!filter.trim()) return unfolded
    // Keep every match and every ancestor of a match, so a filtered plan still reads as
    // an outline rather than as a flat list of orphans.
    const keep = new Set<string>()
    for (const r of unfolded) {
      if (!matchesFilter(r, filter)) continue
      let cursor: Row | undefined = r
      let hops = 0
      while (cursor && hops < 50) {
        keep.add(cursor.id)
        cursor = cursor.parent_id ? byId.get(cursor.parent_id) : undefined
        hops += 1
      }
    }
    return unfolded.filter((r) => keep.has(r.id))
  }, [all, folded, filter])

  /** Select a row the way a spreadsheet does: plain click is one; ctrl adds; shift ranges. */
  const select = useCallback(
    (key: string, e?: { ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean }) => {
      if (e?.shiftKey && selected) {
        const a = visible.findIndex((r) => r.key === selected)
        const b = visible.findIndex((r) => r.key === key)
        if (a >= 0 && b >= 0) {
          const [lo, hi] = a < b ? [a, b] : [b, a]
          setPicked(new Set(visible.slice(lo, hi + 1).map((r) => r.key)))
          return
        }
      }
      if (e?.ctrlKey || e?.metaKey) {
        setPicked((prev) => {
          const next = new Set(prev)
          if (next.has(key)) next.delete(key)
          else next.add(key)
          return next
        })
        setSelected(key)
        return
      }
      setSelected(key)
      setPicked(new Set([key]))
    },
    [selected, visible],
  )

  /** The rows the toolbar acts on: the selection, or the one row, or nothing. */
  const targets = useMemo(() => {
    const keys = picked.size ? [...picked] : selected ? [selected] : []
    // In plan order, so "link the selection" links top to bottom.
    return all.filter((r) => keys.includes(r.key))
  }, [picked, selected, all])

  /** Undo, remembering how to redo it. */
  const undoLast = useCallback(() => {
    const step = history[history.length - 1]
    if (!step || undo.isPending) return
    setRedoStack((r) => [...r.slice(-49), redoStepFor(step, all)])
    undo.mutate(step)
  }, [history, undo, all])

  const redoLast = useCallback(() => {
    const step = redoStack[redoStack.length - 1]
    if (!step || redo.isPending) return
    // Redoing is an edit like any other as far as undo is concerned — but it must not
    // clear the redo stack it came from, so the step goes straight into history.
    setHistory((h) => [...h.slice(-49), redoStepFor(step, all)])
    redo.mutate(step)
  }, [redoStack, redo, all])

  // Every chosen column is shown; the grid scrolls sideways when there are more than
  // fit. Dropping columns for width looked like a bug every time — the one somebody had
  // just ticked was the one that vanished.
  const widths = useColumnWidths('gantt')
  const columns = useMemo(
    () => chosen(choice.keys).map((c) => ({ ...c, width: widths.of(c.key) ?? c.width })),
    [choice.keys, widths],
  )
  const gridWidth = clampSplit(split.width ?? defaultSplit(choice.keys, available), available)
  const paneWidth = Math.max(0, available - gridWidth - SPLITTER_W)
  const colours = useMemo(() => phaseColours(all), [all])

  const geom = useMemo(() => {
    if (!data) return null
    const from = parse(data.from)
    const to = parse(data.to)
    // Never narrower than the pane it sits in: a chart that stops two-thirds of the way
    // across its own box looks broken, and the extra is just more calendar.
    const width = Math.max(paneWidth, daysBetween(from, to) * dayWidth)
    return {
      from,
      to,
      width,
      x: (iso: string) => daysBetween(from, parse(iso)) * dayWidth,
      todayX: daysBetween(from, parse(data.today)) * dayWidth,
      dateAt: (px: number) => addDays(from, Math.round(px / dayWidth)),
    }
  }, [data, dayWidth, paneWidth])

  /* How much room there is decides how many columns the grid gets. Measured rather
     than guessed from a breakpoint: this screen sits inside a project sidebar that is
     itself only there above a certain width. */
  useEffect(() => {
    const frame = frameRef.current
    if (!frame) return
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setAvailable(entry.contentRect.width)
    })
    observer.observe(frame)
    return () => observer.disconnect()
    // Re-run once the plan arrives: the first render is a spinner, so the framed
    // element the observer wants does not exist yet and an empty dependency list
    // would leave it watching nothing for the life of the screen.
  }, [plan.isLoading])

  /* Resizing a column. On the window, like every other drag here, so a fast hand
     that leaves the header does not drop the column at whatever width it had. */
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = colDrag.current
      if (!d) return
      widths.set(d.key, Math.max(44, Math.min(600, Math.round(d.from + (e.clientX - d.fromX)))))
    }
    const onUp = () => {
      colDrag.current = null
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [widths])

  /* No measuring. The frame is exactly the window below the top bar (a CSS calc), and
     the card takes whatever of it the toolbars leave (`flex-1 min-h-0`), so the browser
     sizes it — on load, on resize, with the menu shown or hidden — and the panes'
     scrollbars sit at the foot of the window because that is where the panes end. */

  /* Open on today, a third of the way in — the same place every planner opens — unless
     the plan is entirely behind or ahead of today, in which case open on the plan: a
     finished project opened on an empty September is a screen with nothing on it. Once
     per plan, not on every redraw, or zooming would keep snapping back. */
  const opened = useRef<string | null>(null)
  useEffect(() => {
    const pane = chartPaneRef.current
    if (!pane || !geom || !data || opened.current === data.project.id) return
    opened.current = data.project.id
    const first = all.find((r) => r.start)
    const last = [...all].reverse().find((r) => r.end)
    const todayInside =
      first?.start && last?.end && data.today >= first.start && data.today <= last.end
    if (todayInside) {
      pane.scrollLeft = Math.max(0, geom.todayX - pane.clientWidth / 3)
    } else if (first?.start) {
      pane.scrollLeft = Math.max(0, geom.x(first.start) - 24)
    }
  }, [geom, data, all])


  /* Dragging is handled on the window, not on the bar.

     A pointer that leaves the bar mid-drag must not drop it — a chart where dragging
     fast loses the task is a chart nobody trusts — and the bar itself is a few pixels
     tall, which a hand moving at speed leaves immediately. */
  useEffect(() => {
    if (!drag || !geom) return

    const onMove = (e: PointerEvent) => {
      const box = chartRef.current?.getBoundingClientRect()
      const travelled = Math.abs(e.clientX - drag.fromX) + Math.abs(e.clientY - drag.fromY)
      const live = drag.live || travelled > SLOP
      if (drag.kind === 'link') {
        setDrag({
          ...drag,
          live,
          x: e.clientX - (box?.left ?? 0),
          y: e.clientY - (box?.top ?? 0),
        })
        return
      }
      setDrag({ ...drag, live, days: daysMoved(e.clientX - drag.fromX, dayWidth) })
    }

    const onUp = (e: PointerEvent) => {
      const row = all.find((r) => r.key === drag.key)
      if (drag.kind === 'link') {
        // elementFromPoint, not e.target: with pointer capture the target is the handle
        // for the whole drag, so asking it what is under the cursor answers "itself".
        const under = document.elementFromPoint(e.clientX, e.clientY)
        const to = under?.closest('[data-row-key]')?.getAttribute('data-row-key')
        if (drag.live && to && to !== drag.key) link({ from: drag.key, to })
      } else if (row) {
        const next = resultOf(drag, row)
        // A move says where the task goes, not how long it takes. Sending the finish
        // too made the server take the dragged dates literally, so a bar dropped so
        // that its end landed on a Saturday kept the Saturday — and the task quietly
        // lost or gained a working day. Sending only the start lets the calendar keep
        // the duration. A resize is the opposite: both dates are the point.
        if (next) {
          move(
            drag.kind === 'move'
              ? { key: row.key, start: next.start }
              : { key: row.key, start: next.start, end: next.end },
          )
        }
      }
      setDrag(null)
    }

    // Escape abandons a drag. Without it the only way out of one started by accident is
    // to complete it and then undo.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrag(null)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      window.removeEventListener('keydown', onKey)
    }
  }, [drag, geom, dayWidth, all, link, move, edits.amend])

  /* Dragging a row up or down the grid. Separate from the bar drags because it is a
     different question — where a task sits in the plan, not when it happens. */
  useEffect(() => {
    if (!rowDrag) return
    const rows = all
    const onMove = (e: PointerEvent) => {
      const list = document.querySelector<HTMLElement>('[data-grid-rows]')
      const box = list?.getBoundingClientRect()
      if (!box) return
      setRowDrag({ ...rowDrag, gap: dropGap(e.clientY - box.top, ROW_H, rows.length) })
    }
    const onUp = () => {
      const moving = rows.findIndex((r) => r.key === rowDrag.key)
      // Measured against the list with the dragged row taken out: a row cannot be
      // dropped into the space it already occupies, and offering that as a destination
      // makes the indicator flicker as you pass your own position.
      const others = rows.filter((r) => r.key !== rowDrag.key)
      const gap = rowDrag.gap > moving ? rowDrag.gap - 1 : rowDrag.gap
      const where = landing(
        gap,
        others.map((r) => ({
          key: r.key,
          depth: r.depth,
          has_children: r.has_children,
          parent_key: r.parent_id
            ? (rows.find((p) => p.id === r.parent_id)?.key ?? null)
            : null,
        })),
      )
      const current = rows[moving]
      const currentParent = current?.parent_id
        ? (rows.find((p) => p.id === current.parent_id)?.key ?? null)
        : null
      if (where.after !== rowDrag.key) {
        edits.reorder.mutate({
          key: rowDrag.key,
          after: where.after,
          parent: where.parent,
          reparent: where.parent !== currentParent,
        })
      }
      setRowDrag(null)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setRowDrag(null)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      window.removeEventListener('keydown', onKey)
    }
  }, [rowDrag, all, edits.reorder])

  /* Tab and shift-tab indent, the way every outline in the world does. */
  useEffect(() => {
    /* Indenting is arranging our plan, so the key asks what the menu asks. */
    if (!mayPlan) return
    const onKey = (e: KeyboardEvent) => {
      if (!selected || editing) return
      if (e.key === 'Tab') {
        e.preventDefault()
        const key = selected
        restructure(key, () => edits.structure.mutateAsync({ key, what: e.shiftKey ? 'outdent' : 'indent' }))
      }
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault()
        undoLast()
      }
      if ((e.metaKey || e.ctrlKey) && (e.key.toLowerCase() === 'y' || (e.shiftKey && e.key.toLowerCase() === 'z'))) {
        e.preventDefault()
        redoLast()
      }
      if (e.key === 'Enter' && (e.target as HTMLElement | null)?.tagName !== 'INPUT') {
        e.preventDefault()
        setEditing({ key: selected, field: 'title' })
      }
      if (e.key === 'Insert' && mayPlan) {
        e.preventDefault()
        edits.add
          .mutateAsync({ title: 'New task', after: selected })
          .then((r) => record([{ kind: 'remove', key: r.key }]))
      }
      // Up and down walk the plan; Delete removes; the rest is the grid's own business.
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const at = visible.findIndex((r) => r.key === selected)
        const next = visible[at + (e.key === 'ArrowDown' ? 1 : -1)]
        if (next) {
          e.preventDefault()
          setSelected(next.key)
        }
      }
      if (e.key === 'Delete' && (e.target as HTMLElement | null)?.tagName !== 'INPUT') {
        e.preventDefault()
        /*
          The key was the way past the menu. Whoever cannot see Delete could still press it and
          watch the API refuse, which reads as the screen being broken rather than the rule
          holding. Silent when nothing may go, for the same reason the menu entry is absent.
        */
        for (const t of targets.filter((t) => t.may_delete)) edits.remove.mutate(t.key)
      }
      if (e.key === 'Escape') {
        setPicked(new Set())
        setMenu(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selected, editing, writable, mayPlan, edits.structure, edits.remove, edits.add, visible, restructure, undoLast, redoLast, targets, record])

  if (plan.isLoading || !data || !geom) {
    return <div className="grid place-items-center py-20"><Spinner /></div>
  }

  const rows = visible
  const index = rows.findIndex((r) => r.key === selected)
  const peeked = peek ? rows.find((r) => r.key === peek.key) : undefined

  return (
    // What a working day is worth here, so every length on the screen can be said in
    // hours as well as days without handing it down through six components.
    <DayHoursProvider value={dayHours(plan.data?.day_hours)}>
    <div
      ref={frameRef}
      // Exactly the window below the top bar and the page's padding, so the card can take
      // the rest with flex-1 and the browser does the arithmetic on every resize. Only
      // from md up: narrow screens carry the project tabs across the top and read best
      // as a page that scrolls.
      className="flex flex-col gap-3 md:h-[calc(100dvh-var(--shell-header)-3rem)]"
    >
      <PageHeader
        title="Schedule"
        subtitle="Type in the grid, drag on the chart. Moving a task moves what waits for it."
        actions={
          <div className="flex flex-wrap items-center gap-1">
            <Tool label="Zoom out" disabled={zoom.percent === ZOOM_STEPS[0]} onClick={() => zoom.step(-1)}>
              <ZoomOutIcon />
            </Tool>
            <Menu
              width={140}
              label="Gantt zoom"
              trigger={(props) => (
                <Tip label="Zoom — how much calendar a day gets">
                  <button
                    type="button"
                    {...props}
                    className="inline-flex h-7 items-center gap-1 rounded-[5px] border border-[var(--color-rule)] bg-[var(--color-surface)] px-2 text-[12px] tabular-nums hover:bg-[var(--color-surface-sunk)]"
                  >
                    {zoom.percent}%
                    <ChevronDownIcon size={12} />
                  </button>
                </Tip>
              )}
            >
              {(close) => (
                <>
                  {ZOOM_STEPS.map((p) => (
                    <MenuItem
                      key={p}
                      onClick={() => {
                        zoom.set(p)
                        close()
                      }}
                    >
                      <span className={clsx('tabular-nums', p === zoom.percent && 'font-semibold')}>{p}%</span>
                    </MenuItem>
                  ))}
                </>
              )}
            </Menu>
            <Tool
              label="Zoom in"
              disabled={zoom.percent === ZOOM_STEPS[ZOOM_STEPS.length - 1]}
              onClick={() => zoom.step(1)}
            >
              <ZoomInIcon />
            </Tool>
            <Tool
              label="Fit the whole plan in the window"
              onClick={() => {
                const pane = chartPaneRef.current
                if (!pane || !data) return
                const days = Math.max(1, daysBetween(parse(data.from), parse(data.to)))
                const want = ((pane.clientWidth - 24) / days / BASE_DAY) * 100
                const fit = [...ZOOM_STEPS].reverse().find((z) => z <= want) ?? ZOOM_STEPS[0]!
                zoom.set(fit)
              }}
            >
              <FitIcon />
            </Tool>
            <Tool
              label="Scroll to today"
              onClick={() => {
                const pane = chartPaneRef.current
                if (pane && geom) pane.scrollTo({ left: Math.max(0, geom.todayX - pane.clientWidth / 3), behavior: 'smooth' })
              }}
            >
              <TodayIcon />
            </Tool>
            <Divider />
            <Tool
              label={showCritical ? 'Hide the critical path' : 'Show the critical path'}
              active={showCritical}
              onClick={() => setShowCritical(!showCritical)}
            >
              <CriticalIcon />
            </Tool>
            {/* Rescheduling the whole plan is planning at its most wholesale — see `mayPlan`. */}
            {mayPlan && (
              <Tool
                label="Close the gaps — pull every task back to the earliest its links and rules allow"
                disabled={edits.tighten.isPending}
                onClick={() => edits.tighten.mutateAsync().then((r) => remember(r.moved))}
              >
                <TightenIcon />
              </Tool>
            )}
            {/*
              `mayPlan`, not `writable`. Undo and redo walk back the edits this screen makes, and
              a customer can no longer make any — so for them these were two buttons that would
              stay disabled forever. A control that can never come alive is not an affordance.
            */}
            {mayPlan && (
              <Tool
                label={
                  history.length
                    ? `Undo (${history.length} ${history.length === 1 ? 'change' : 'changes'} to go back through) — Ctrl+Z`
                    : 'Nothing to undo yet'
                }
                disabled={history.length === 0 || undo.isPending}
                onClick={undoLast}
              >
                <UndoIcon />
              </Tool>
            )}
            {mayPlan && (
              <Tool
                label={redoStack.length ? `Redo — Ctrl+Y` : 'Nothing to redo'}
                disabled={redoStack.length === 0 || redo.isPending}
                onClick={redoLast}
              >
                <RedoIcon />
              </Tool>
            )}
            <Divider />
            <Tool
              label={density.density === 'compact' ? 'Comfortable rows' : 'Compact rows — more of the plan on the screen'}
              active={density.density === 'compact'}
              onClick={density.toggle}
            >
              <DensityIcon />
            </Tool>
            {/*
              CLOSED work is hidden by default — not finished work, which is a distinction the
              first attempt got wrong. A task that is done but still on the board stays on the
              chart: it is the bar that proves the work after it could start, and hiding it
              leaves a gap that reads as missing rather than finished.
              
              Same word and same toggle as the board's "show closed".
            */}
            <Tip
              label={
                showClosed
                  ? 'Hide work that has been closed and filed away'
                  : 'Show work that has been closed and filed away as well'
              }
            >
              <button
                type="button"
                aria-pressed={showClosed}
                onClick={() => setShowClosed(!showClosed)}
                className={clsx(
                  'inline-flex h-7 items-center rounded-[5px] border px-2 text-[12px]',
                  showClosed
                    ? 'border-[var(--color-accent)] text-[var(--color-ink)]'
                    : 'border-[var(--color-rule)] text-[var(--color-ink-dim)]',
                )}
              >
                {showClosed ? 'Hide closed' : 'Show closed'}
              </button>
            </Tip>
            <Tip label="Filter the plan — title, key or owner; every word must match">
              <span className="inline-flex h-7 items-center gap-1 rounded-[5px] border border-[var(--color-rule)] bg-[var(--color-surface)] px-1.5 text-[var(--color-ink-faint)] focus-within:border-[var(--color-accent)]">
                <FilterIcon />
                <input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter"
                  aria-label="Filter the plan"
                  /*
                    THE APP'S OWN INPUT STYLING HAS TO BE UNDONE HERE, OR THIS LOOKS LIKE TWO
                    FIELDS.
                    
                    `theme.css` styles every bare `input` with a border, an 8px radius, a
                    background and `min-height: 34px`. This one lives inside a 28px toolbar
                    control that draws its own border around the funnel and the box together —
                    so the input drew a SECOND bordered box, taller than its container, bursting
                    out of it top and bottom. Focus made it worse: the wrapper lit up on
                    `focus-within` and the input lit up on `:focus`, giving two concentric
                    rings.
                    
                    Every reset below is aimed at one declaration in that rule. `focus:` is
                    needed on the shadow because `input:focus` outscores a plain utility.
                  */
                  className="plan-inline-field text-[12px] text-[var(--color-ink)] placeholder:text-[var(--color-ink-faint)]"
                />
                {filter && (
                  <button type="button" aria-label="Clear the filter" className="text-[11px]" onClick={() => setFilter('')}>
                    ×
                  </button>
                )}
              </span>
            </Tip>
            <Divider />
            <ColumnChooser choice={choice} />
            {/*
              The browser's own print, not a route.

              The kit had a dedicated print screen at `/p/{key}/schedule/print`, which was
              never ported — so this button navigated to the catch-all and the chart vanished,
              which is worse than no button. `window.print()` gives the same two outcomes the
              label promises, paper and PDF, from the chart already on screen.
            */}
            <Tool label="Print, or save as a PDF" onClick={() => window.print()}>
              <PrintIcon />
            </Tool>
            {/* Microsoft Project XML, in and out. */}
            {/* Export is everybody's; importing a whole plan is ours — see `mayPlan`. */}
            <Interchange projectId={scopeTo} writable={mayPlan} />
            {/* By id, not by key. The kit's own path was `/p/{projectKey}/roadmap`, which
                would have needed a code-to-id lookup nothing else in the Hub does — and
                until this changed the button navigated to a route that does not exist. */}
            <Tool
              label="The slide deck this plan generates"
              onClick={() => navigate({ to: '/projects/$projectId/roadmap', params: { projectId: data.project.id } })}
            >
              <DeckIcon />
            </Tool>
          </div>
        }
      />

      {/* Every tool on it arranges the plan — add, indent, move, link, delete. */}
      {mayPlan && (
        <PlanToolbar
          selected={selected}
          count={targets.length}
          rows={rows}
          index={index}
          canAdd={mayPlan}
          onAdd={() =>
            edits.add
              .mutateAsync({ title: 'New task', after: selected ?? undefined })
              .then((r) => record([{ kind: 'remove', key: r.key }]))
          }
          onIndent={() => {
            for (const t of targets) restructure(t.key, () => edits.structure.mutateAsync({ key: t.key, what: 'indent' }))
          }}
          onOutdent={() => {
            for (const t of targets) restructure(t.key, () => edits.structure.mutateAsync({ key: t.key, what: 'outdent' }))
          }}
          onLink={
            targets.length >= 2
              ? () => {
                  // Top to bottom, finish to start: the way a run of selected tasks is
                  // read, and the way Project links a selection.
                  const leaves = targets.filter((t) => !t.has_children)
                  for (let i = 1; i < leaves.length; i += 1) link({ from: leaves[i - 1]!.key, to: leaves[i]!.key })
                }
              : undefined
          }
          onUnlink={
            targets.some((t) => (t.predecessors ?? []).length)
              ? () => {
                  for (const t of targets) {
                    for (const p of t.predecessors ?? []) {
                      edits.unlink.mutateAsync({ from: p.key, to: t.key }).then(() =>
                        record([{ kind: 'link', from: p.key, to: t.key, relation: p.relation, lag: p.lag_days }]),
                      )
                    }
                  }
                }
              : undefined
          }
          onUp={() =>
            selected &&
            restructure(selected, () =>
              edits.reorder.mutateAsync({ key: selected, after: index > 1 ? rows[index - 2]!.key : null }),
            )
          }
          onDown={() =>
            selected &&
            index < rows.length - 1 &&
            restructure(selected, () => edits.reorder.mutateAsync({ key: selected, after: rows[index + 1]!.key }))
          }
          /*
            WHO MAY DELETE, not whether this selection happens to be deletable.

            With nothing picked it stays visible and disabled, which is what tells somebody the
            action exists and needs a row. It disappears only for a person who may never use it,
            or on a selection where something in it may not go — deleting four of six is worse
            than not offering.
          */
          canDelete={targets.length === 0 ? mayDelete : targets.every((t) => t.may_delete)}
          onDelete={() => {
            for (const t of targets.filter((t) => t.may_delete)) edits.remove.mutate(t.key)
          }}
        />
      )}

      {rows.length === 0 ? (
        <EmptyState
          title="Nothing planned yet"
          body="Add a task, give it a start and a length, and it appears on the chart. Drag one bar onto another to say that one waits for the other."
        />
      ) : (
        <div ref={cardRef} className="card flex min-h-0 flex-1 flex-col overflow-hidden">
          <div ref={panesRef} className="flex min-h-0 flex-1">
            {/* The grid, in a pane of its own. It scrolls sideways on its own bar and up
                and down in step with the chart — the chart's bar does the vertical
                scrolling for both, and a wheel over the grid drives the same bar. */}
            <div
              ref={gridPaneRef}
              className="shrink-0 overflow-x-auto overflow-y-hidden"
              style={{ width: gridWidth }}
              onWheel={(e) => {
                const pane = chartPaneRef.current
                if (pane && Math.abs(e.deltaY) > Math.abs(e.deltaX)) pane.scrollTop += e.deltaY
              }}
              onScroll={(e) => {
                // The grid cannot be wheeled up and down, but the browser still scrolls
                // it to bring a focused cell or a clicked row into view. The chart has to
                // follow, or the two panes drift apart by a screenful.
                const chart = chartPaneRef.current
                if (chart && chart.scrollTop !== e.currentTarget.scrollTop) chart.scrollTop = e.currentTarget.scrollTop
              }}
            >
            <Grid
              columns={columns}
              mayPlan={mayPlan}
              contentWidth={columns.reduce((sum, c) => sum + c.width, 0) + 20 + NUMBER_W + COLUMN_ADD_W}
              colours={colours}
              rows={rows}
              folded={folded}
              hovered={hovered}
              onHover={setHovered}
              onFold={(key) =>
                setFolded((f) => {
                  const next = new Set(f)
                  if (next.has(key)) next.delete(key)
                  else next.add(key)
                  return next
                })
              }
              onResizeStart={(key, clientX, width) => {
                colDrag.current = { key, fromX: clientX, from: width }
              }}
              onResize={(key, width) => widths.set(key, width)}
              rowDrag={rowDrag}
              onRowDragStart={(key) => setRowDrag({ key, gap: rows.findIndex((r) => r.key === key) })}
              users={users.data ?? []}
              selected={selected}
              picked={picked}
              editing={editing}
              writable={writable}
              axisHeight={TODAY_LANE + LABEL_LANE}
              showCritical={showCritical}
              onSelect={select}
              onContext={(key, x, y) => {
                if (!picked.has(key)) select(key)
                setMenu({ key, x, y })
              }}
              onColumnMenu={(x, y, key) => setColumnMenu({ x, y, key })}
              onEdit={setEditing}
              onCommit={(key, body) => {
                setEditing(null)
                commit(key, body)
              }}
              /* Enter at the end of a title adds the next row — ours to do, like every other add. */
              onAddBelow={(key) =>
                mayPlan &&
                edits.add
                  .mutateAsync({ title: 'New task', after: key })
                  .then((r) => record([{ kind: 'remove', key: r.key }]))
              }
              onOpen={openByKey}
              projectId={scopeTo}
              onLink={(input) => {
                // Changing a link that exists is undone by putting the old one back;
                // adding one is undone by removing it.
                const row = all.find((r) => r.key === input.to)
                const had = row?.predecessors?.find((p) => p.key === input.from)
                edits.link.mutateAsync(input).then((r) =>
                  record([
                    had
                      ? { kind: 'link', from: input.from, to: input.to, relation: had.relation, lag: had.lag_days }
                      : { kind: 'unlink', from: input.from, to: input.to },
                    ...(r.moved.length ? [{ kind: 'moves', moved: r.moved } as UndoAction] : []),
                  ]),
                )
              }}
              onUnlink={(from, to) => {
                const row = all.find((r) => r.key === to)
                const had = row?.predecessors?.find((p) => p.key === from)
                edits.unlink.mutateAsync({ from, to }).then(() => {
                  if (had) record([{ kind: 'link', from, to, relation: had.relation, lag: had.lag_days }])
                })
              }}
              onTick={(key, done) => void tickOff(key, done)}
            />
            </div>

            {/* The divider. Drag it to give the grid more room or the chart more; double
                click puts it back where the chosen columns would have it. */}
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Drag to resize the grid"
              title="Drag to resize · double-click to reset"
              className="group relative shrink-0 cursor-col-resize touch-none select-none bg-[var(--color-rule)] hover:bg-[var(--color-accent)]"
              style={{ width: SPLITTER_W }}
              onPointerDown={(e) => {
                e.preventDefault()
                ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
                splitDrag.current = { fromX: e.clientX, from: gridWidth }
              }}
              onPointerMove={(e) => {
                if (!splitDrag.current) return
                split.set(clampSplit(splitDrag.current.from + (e.clientX - splitDrag.current.fromX), available))
              }}
              onPointerUp={() => {
                splitDrag.current = null
              }}
              onPointerCancel={() => {
                splitDrag.current = null
              }}
              onDoubleClick={() => split.set(null)}
            />

            {/* The chart, in its own pane. Both scrollbars are this pane's: sideways for
                the calendar, and up and down for both panes together. `isolate` gives
                it a stacking context, so a bar can sit above the dependency overlay
                without also rising above the grid beside it. */}
            <div
              ref={chartPaneRef}
              className="min-w-0 flex-1 overflow-auto"
              onScroll={(e) => {
                const grid = gridPaneRef.current
                if (grid && grid.scrollTop !== e.currentTarget.scrollTop) grid.scrollTop = e.currentTarget.scrollTop
              }}
            >
            <div
              ref={chartRef}
              data-chart
              className="relative isolate"
              style={{ width: geom.width }}
              onPointerDown={() => setError(null)}
            >
              <div className="sticky top-0 z-[5]">
                <Axis
                  from={geom.from}
                  to={geom.to}
                  width={geom.width}
                  dayWidth={dayWidth}
                  height={TODAY_LANE + LABEL_LANE}
                />
              </div>
              <div className="relative" onPointerLeave={() => setHovered(null)}>
                <Background
                  rows={rows.length}
                  from={geom.from}
                  width={geom.width}
                  dayWidth={dayWidth}
                  holidays={data.holidays ?? []}
                  todayX={geom.todayX}
                />
                {rows.map((row, i) => (
                  <div
                    key={`strip-${row.id}`}
                    data-row-key={row.key}
                    className={clsx(
                      'absolute inset-x-0 z-[1]',
                      hovered === row.key && 'bg-[var(--color-ink)]/[0.04]',
                      (picked.has(row.key) || selected === row.key) && 'bg-[var(--color-accent-soft)]/60',
                    )}
                    style={{ top: i * ROW_H, height: ROW_H }}
                    onClick={(e) => select(row.key, e)}
                    onContextMenu={(e) => {
                      e.preventDefault()
                      if (!picked.has(row.key)) select(row.key)
                      setMenu({ key: row.key, x: e.clientX, y: e.clientY })
                    }}
                    onPointerEnter={() => setHovered(row.key)}
                  />
                ))}
                <Dependencies
                  rows={rows}
                  links={data.dependencies}
                  x={geom.x}
                  dayWidth={dayWidth}
                  width={geom.width}
                  onRemove={(from, to) => writable && edits.unlink.mutate({ from, to })}
                />
                {rows.map((row, i) => (
                  <Bar
                    key={row.id}
                    row={row}
                    phase={colours.get(row.key) ?? 'var(--gantt-epic)'}
                    top={i * ROW_H}
                    x={geom.x}
                    dayWidth={dayWidth}
                    drag={drag}
                    writable={writable}
                    selected={picked.has(row.key) || selected === row.key}
                    showCritical={showCritical}
                    onSelect={(e) => select(row.key, e)}
                    onContext={(x, y) => {
                      if (!picked.has(row.key)) select(row.key)
                      setMenu({ key: row.key, x, y })
                    }}
                    onPeek={(at) => setPeek(at ? { key: row.key, ...at } : null)}
                    onDragStart={(kind, e) => {
                      e.preventDefault()
                      // Capture on the element the press landed on, so the whole drag
                      // belongs to it even when the pointer runs off the chart.
                      ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
                      const box = chartRef.current?.getBoundingClientRect()
                      setDrag({
                        kind,
                        key: row.key,
                        fromX: e.clientX,
                        fromY: e.clientY,
                        x: e.clientX - (box?.left ?? 0),
                        y: e.clientY - (box?.top ?? 0),
                        days: 0,
                        live: false,
                      })
                    }}
                  />
                ))}
                {drag?.kind === 'link' && <LinkGhost drag={drag} rows={rows} x={geom.x} />}
                {drag?.live && drag.kind !== 'link' && (
                  <DragReadout drag={drag} rows={rows} x={geom.x} dayWidth={dayWidth} />
                )}
                {peeked && !drag && <Peek row={peeked} at={peek!} />}
              </div>
              <TodayMarker
                left={geom.todayX}
                height={TODAY_LANE + LABEL_LANE + rows.length * ROW_H}
              />
            </div>
            </div>
          </div>
          <Legend showCritical={showCritical} hasBaseline={rows.some((r) => r.baseline_start)} />
        </div>
      )}

      {columnMenu && (
        <ContextMenu
          at={columnMenu}
          onClose={() => setColumnMenu(null)}
          entries={columnMenuEntries(choice, columnMenu.key)}
        />
      )}
      {menu && (
        <ContextMenu
          at={menu}
          onClose={() => setMenu(null)}
          entries={contextEntries({
            key: menu.key,
            rows: all,
            targets,
            writable,
            mayPlan,
            onAddBelow: (key) =>
              edits.add.mutateAsync({ title: 'New task', after: key }).then((r) => record([{ kind: 'remove', key: r.key }])),
            onInsertAbove: (key) => {
              // Above the row itself, which the server can place even when there is
              // nothing above it. Asking for "after the row above" put a task inserted
              // above the first one at the very bottom of the plan.
              edits.add
                .mutateAsync({ title: 'New task', before: key })
                .then((r) => record([{ kind: 'remove', key: r.key }]))
            },
            onIndent: () => {
              for (const t of targets) restructure(t.key, () => edits.structure.mutateAsync({ key: t.key, what: 'indent' }))
            },
            onOutdent: () => {
              for (const t of targets) restructure(t.key, () => edits.structure.mutateAsync({ key: t.key, what: 'outdent' }))
            },
            onLinkSelection: () => {
              const leaves = targets.filter((t) => !t.has_children)
              for (let i = 1; i < leaves.length; i += 1) link({ from: leaves[i - 1]!.key, to: leaves[i]!.key })
            },
            onUnlink: (key) => {
              const row = all.find((r) => r.key === key)
              for (const p of row?.predecessors ?? []) {
                edits.unlink.mutateAsync({ from: p.key, to: key }).then(() =>
                  record([{ kind: 'link', from: p.key, to: key, relation: p.relation, lag: p.lag_days }]),
                )
              }
            },
            onTick: (key, done) => void tickOff(key, done),
            onOpen: openByKey,
            onDelete: () => {
              for (const t of targets.filter((t) => t.may_delete)) edits.remove.mutate(t.key)
            },
          })}
        />
      )}

      <ErrorNote error={error} />
    </div>
    </DayHoursProvider>
  )
}

/* ---------------------------------------------------------------- the right-click menu */

function contextEntries(a: {
  key: string
  rows: Row[]
  targets: Row[]
  writable: boolean
  /** Whether this plan's SHAPE is theirs to change — asked by side, not by capability. */
  mayPlan: boolean
  onAddBelow: (key: string) => void
  onInsertAbove: (key: string) => void
  onIndent: () => void
  onOutdent: () => void
  onLinkSelection: () => void
  onUnlink: (key: string) => void
  onTick: (key: string, done: boolean) => void
  onOpen: (key: string) => void
  onDelete: () => void
}): MenuEntry[] {
  const row = a.rows.find((r) => r.key === a.key)
  if (!row) return []
  const many = a.targets.length > 1
  const label = many ? `${a.targets.length} tasks` : row.key
  const done = row.status_category === 'done'
  /**
   * ASKED OF THE ROW. `writable` is whether this project can be planned at all; `editable`,
   * `may_move` and `may_delete` are what this reader may do to THIS task, and the API answers
   * all three per row. A locked task in a running sprint now gets the short menu — the one that
   * only opens it — the same way the board shows it locked.
   */
  if (!a.writable || !row.editable) {
    return [
      { kind: 'heading', label: row.key },
      { kind: 'item', label: 'Open the task', icon: <OpenIcon />, onSelect: () => a.onOpen(row.key) },
    ]
  }
  return [
    { kind: 'heading', label },
    /*
      THE SHAPE OF THE PLAN IS OURS — all of it, not only the adding.

      Putting work on the plan, arranging what is on it, and saying what waits for what are one
      question with one answer, and `mayPlan` is it. A customer could not set a due date here and
      yet could re-parent our delivery and draw dependencies across it, which is a stranger
      permission than either half sounds. Their route in is "Report it".
    */
    ...(a.mayPlan
      ? [
          { kind: 'item' as const, label: 'Add a task below', icon: <AddIcon />, shortcut: 'Ins', onSelect: () => a.onAddBelow(row.key) },
          { kind: 'item' as const, label: 'Insert a task above', icon: <InsertAboveIcon />, onSelect: () => a.onInsertAbove(row.key) },
          { kind: 'divider' as const },
          { kind: 'item' as const, label: many ? 'Indent these' : 'Indent', icon: <IndentIcon />, shortcut: 'Tab', onSelect: a.onIndent },
          { kind: 'item' as const, label: many ? 'Outdent these' : 'Outdent', icon: <OutdentIcon />, shortcut: '⇧Tab', onSelect: a.onOutdent },
          { kind: 'divider' as const },
          {
            kind: 'item' as const,
            label: 'Link the selected tasks, top to bottom',
            icon: <LinkIcon />,
            disabled: a.targets.filter((t) => !t.has_children).length < 2,
            onSelect: a.onLinkSelection,
          },
          {
            kind: 'item' as const,
            label: 'Stop waiting for anything',
            icon: <UnlinkIcon />,
            disabled: !(row.predecessors ?? []).length,
            onSelect: () => a.onUnlink(row.key),
          },
          { kind: 'divider' as const },
        ]
      : []),
    /* Ticking it off IS moving it to Done, so it asks `work.move` — never a customer's. */
    ...(row.has_children || !row.may_move
      ? []
      : [
          {
            kind: 'item' as const,
            label: done ? 'Reopen' : 'Tick it off',
            icon: <TickIcon />,
            onSelect: () => a.onTick(row.key, !done),
          },
        ]),
    { kind: 'item', label: 'Open the task', icon: <OpenIcon />, onSelect: () => a.onOpen(row.key) },
    /*
      Delete is left out, not greyed: `work.delete` is staff-only, and with several selected it
      is offered only when every one of them may go. A menu that deletes four of six is worse
      than one that does not offer to.
    */
    ...(a.targets.every((t) => t.may_delete)
      ? [
          { kind: 'divider' as const },
          {
            kind: 'item' as const,
            label: many ? `Delete ${a.targets.length} tasks` : 'Delete',
            icon: <TrashIcon />,
            shortcut: 'Del',
            danger: true,
            onSelect: a.onDelete,
          },
        ]
      : []),
  ]
}

/* ------------------------------------------------------------------------- grid */

/**
 * The grid beside the chart.
 *
 * Sticky, because a bar with no name beside it is decoration. Column-driven, because
 * the chart is the reason somebody opened this screen and a grid wide enough to push it
 * off the right-hand edge has stopped helping.
 */
function Grid({
  columns,
  rows,
  mayPlan,
  rowDrag,
  onRowDragStart,
  users,
  selected,
  picked,
  editing,
  writable,
  axisHeight,
  showCritical,
  onSelect,
  onContext,
  onColumnMenu,
  onEdit,
  onCommit,
  onAddBelow,
  onOpen,
  projectId,
  onLink,
  onUnlink,
  onTick,
  contentWidth,
  colours,
  folded,
  onFold,
  hovered,
  onHover,
  onResizeStart,
  onResize,
}: {
  columns: Column[]
  /** A drag on a column edge has begun: which column, where the pointer was, how wide. */
  onResizeStart: (key: string, clientX: number, width: number) => void
  /** A column's width has been set — or, with null, put back to its usual width. */
  onResize: (key: string, width: number | null) => void
  /** Phases folded shut, by key. */
  folded: Set<string>
  onFold: (key: string) => void
  hovered: string | null
  onHover: (key: string | null) => void
  /** Every column side by side, so the pane knows how far it scrolls. */
  contentWidth: number
  /** The phase colour for each row, by key. */
  colours: Map<string, string>
  rows: Row[]
  rowDrag: { key: string; gap: number } | null
  onRowDragStart: (key: string, e: React.PointerEvent) => void
  users: { value: string; label: string }[]
  selected: string | null
  /** Every selected row, when there is more than one. */
  picked: Set<string>
  editing: { key: string; field: string } | null
  writable: boolean
  /** Whether this plan's shape is theirs to arrange — see the caller. */
  mayPlan: boolean
  axisHeight: number
  showCritical: boolean
  onSelect: (key: string, e?: { ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean }) => void
  /** A right-click on a row: which row, and where on the screen. */
  onContext: (key: string, x: number, y: number) => void
  /** The column menu: where to open it, and which column was under the pointer, if any. */
  onColumnMenu: (x: number, y: number, key?: string) => void
  onEdit: (cell: { key: string; field: string } | null) => void
  onCommit: (key: string, body: Record<string, unknown>) => void
  onAddBelow: (key: string) => void
  onOpen: (key: string) => void
  projectId: string
  onLink: (input: { from: string; to: string; relation: string; lag: number }) => void
  onUnlink: (from: string, to: string) => void
  onTick: (key: string, done: boolean) => void
}) {
  const perDay = useDayHours()
  // Everything up to and including the task name stays put while the rest scrolls
  // sideways. Which columns those are depends on what is switched on — WBS sits before
  // the name when it is showing — so it is the leading run, not a fixed pair.
  const titleAt = columns.findIndex((c) => c.key === 'title')
  const pinnedColumns = titleAt < 0 ? [] : columns.slice(0, titleAt + 1)
  const scrollingColumns = titleAt < 0 ? columns : columns.slice(titleAt + 1)

  return (
    <div className="bg-[var(--color-surface)]" style={{ width: contentWidth }} onPointerLeave={() => onHover(null)}>
      <div
        className="sticky top-0 z-[2] flex items-end border-b-2 border-[var(--gantt-axis-rule)] bg-[var(--gantt-axis-band)] text-[11px] font-semibold tracking-wide text-[var(--color-ink-dim)] uppercase"
        style={{ height: axisHeight }}
        title="Right-click to show or hide columns"
        onContextMenu={(e) => {
          e.preventDefault()
          onColumnMenu(e.clientX, e.clientY)
        }}
      >
        {/* The number and the task name stay put while the rest of the grid scrolls.
            Scrolled sideways to reach the cost column, a plan lost the one column that
            says which row you are looking at. */}
        <span
          className="sticky left-0 z-[2] flex items-end bg-[var(--gantt-axis-band)] shadow-[2px_0_5px_-3px_rgba(0,0,0,0.35)]"
          style={{ height: axisHeight }}
        >
          <span
            className="flex shrink-0 items-end justify-center pb-1.5 text-[10px] text-[var(--color-ink-faint)]"
            style={{ width: NUMBER_W }}
          >
            #
          </span>
          <span className="w-5 shrink-0" />
          {pinnedColumns.map((column) => (
            <span
              key={column.key}
              className="relative flex min-w-0 items-end px-2 pb-1.5"
              style={{ width: column.width }}
              onContextMenu={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onColumnMenu(e.clientX, e.clientY, column.key)
              }}
            >
              <span className="min-w-0 truncate">{column.label}</span>
              <span
                role="separator"
                aria-orientation="vertical"
                aria-label={`Resize the ${column.label} column`}
                className="absolute inset-y-0 right-0 z-[1] w-2 cursor-col-resize touch-none select-none border-r border-transparent hover:border-[var(--color-accent)] hover:bg-[var(--color-accent)]/20"
                onPointerDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
                  onResizeStart(column.key, e.clientX, column.width)
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation()
                  onResize(column.key, null)
                }}
              />
            </span>
          ))}
        </span>
        {scrollingColumns.map((column) => (
          <span
            key={column.key}
            className="relative flex min-w-0 items-end px-2 pb-1.5"
            style={{ width: column.width }}
            onContextMenu={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onColumnMenu(e.clientX, e.clientY, column.key)
            }}
          >
            <span className="min-w-0 truncate">{column.label}</span>
            {/* The edge you drag to make the column wider or narrower. Double-click
                puts it back to its usual width. Kept inside the cell: a handle that
                hangs over the edge is clipped by the cell's overflow and the half that
                is left is easy to miss. */}
            <span
              role="separator"
              aria-orientation="vertical"
              aria-label={`Resize the ${column.label} column`}
              className="absolute inset-y-0 right-0 z-[1] w-2 cursor-col-resize touch-none select-none border-r border-transparent hover:border-[var(--color-accent)] hover:bg-[var(--color-accent)]/20"
              onPointerDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
                onResizeStart(column.key, e.clientX, column.width)
              }}
              onDoubleClick={(e) => {
                e.stopPropagation()
                onResize(column.key, null)
              }}
            />
          </span>
        ))}
        {/* The + every planning tool puts at the end of its header. The toolbar has the
            same chooser, but this is where a person hunting for a column looks. */}
        <button
          type="button"
          aria-label="Add or hide columns"
          title="Add or hide columns"
          className="mb-1 ml-1 flex size-5 shrink-0 items-center justify-center rounded border border-[var(--color-rule)] text-[13px] leading-none text-[var(--color-ink-dim)] normal-case hover:border-[var(--color-accent)] hover:text-[var(--color-ink)]"
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect()
            onColumnMenu(r.left, r.bottom + 4)
          }}
        >
          +
        </button>
      </div>

      <div data-grid-rows className="relative">

      {rows.map((row, i) => (
        <div
          key={row.id}
          data-row-key={row.key}
          onClick={(e) => onSelect(row.key, e)}
          onContextMenu={(e) => {
            e.preventDefault()
            onContext(row.key, e.clientX, e.clientY)
          }}
          onPointerEnter={() => onHover(row.key)}
          className={clsx(
            'relative flex items-center border-b border-[var(--color-rule)] text-[12px] select-none',
            i % 2 === 1 && 'bg-[var(--gantt-zebra)]',
            hovered === row.key && 'bg-[var(--color-ink)]/[0.04]',
            (picked.has(row.key) || selected === row.key) && 'bg-[var(--color-accent-soft)]',
          )}
          style={{ height: ROW_H }}
        >
          {/* The phase's colour, down the edge of every row in it — the same colour
              its bars are drawn in, so the grid and the chart read as one thing. */}
          <span
            aria-hidden
            className="absolute inset-y-0 left-0 w-[3px]"
            style={{ background: colours.get(row.key) }}
          />
          {/* The name of the row stays put while the rest of the grid scrolls: the
              one column that says which row you are looking at should never be the
              one that scrolls away. */}
          {(() => {
            /**
             * WHAT MAY BE DONE TO THIS ROW, as opposed to whether this project can be planned.
             *
             * Every cell below took the screen-wide `writable`, so a task locked by a running
             * sprint — one the board drew greyed out and refused to save — still offered a live
             * title, dates and a length here. `editable` is the board's own answer, per row.
             */
            const mayEdit = writable && row.editable
            /* Dragging a row moves it in OUR outline, which is planning, not editing. */
            const mayArrange = mayEdit && mayPlan
            const cellFor = (column: Column) => {
                if (column.key === 'title') {
                  return (
                    <Cell key={column.key} width={column.width}>
                      <span
                        className="flex min-w-0 items-center gap-1"
                        style={{ paddingLeft: row.depth * 12 }}
                      >
                        {row.has_children && (
                          <button
                            type="button"
                            aria-label={folded.has(row.key) ? `Open ${row.title}` : `Fold ${row.title}`}
                            aria-expanded={!folded.has(row.key)}
                            className="-ml-1 flex size-4 shrink-0 items-center justify-center rounded text-[10px] text-[var(--color-ink-dim)] hover:bg-[var(--color-surface-sunk)]"
                            onClick={(e) => {
                              e.stopPropagation()
                              onFold(row.key)
                            }}
                          >
                            {folded.has(row.key) ? '▸' : '▾'}
                          </button>
                        )}
                        {showCritical && row.critical && !row.has_children && (
                          <span
                            aria-label="on the critical path"
                            className="size-1.5 shrink-0 rounded-full"
                            style={{ background: 'var(--gantt-critical)' }}
                          />
                        )}
                        <RuleMarks row={row} />
                        <Editable
                          value={row.title}
                          active={editing?.key === row.key && editing.field === 'title'}
                          writable={mayEdit}
                          bold={row.has_children}
                          onStart={() => onEdit({ key: row.key, field: 'title' })}
                          onCommit={(v) => onCommit(row.key, { title: v })}
                          onCancel={() => onEdit(null)}
                          onEnter={() => onAddBelow(row.key)}
                        />
                      </span>
                    </Cell>
                  )
                }
                if (column.key === 'assignee') {
                  /**
                   * ONE COLUMN, AND A TASK CAN HOLD SEVERAL PEOPLE.
                   *
                   * The cell used to show the first name and let you pick another, and picking
                   * one REPLACED THE WHOLE LIST — three people on a task became one, silently.
                   * So it now says how many there are, and on anything with more than one it
                   * stops being editable here and sends you to the popup, which can show them
                   * all. The single-person case is the common one and stays as fast as it was.
                   */
                  const several = row.assignees.length > 1
                  const cell = (
                    <button
                      type="button"
                      className="w-full truncate text-left disabled:cursor-default"
                      disabled={!mayEdit || several}
                      onClick={() => onEdit({ key: row.key, field: 'assignee' })}
                    >
                      {row.assignees.length === 0 ? (
                        <span className="text-[var(--color-ink-faint)]">—</span>
                      ) : (
                        <>
                          {row.assignees[0].name}
                          {several && (
                            <span className="text-[var(--color-ink-faint)]"> +{row.assignees.length - 1}</span>
                          )}
                        </>
                      )}
                    </button>
                  )
                  return (
                    <Cell key={column.key} width={column.width}>
                      {editing?.key === row.key && editing.field === 'assignee' && !several ? (
                        <Picker
                          openOnMount
                          options={users}
                          value={row.assignee_id}
                          onChange={(v) => onCommit(row.key, { assignee_id: v })}
                        />
                      ) : several ? (
                        /*
                          THE REST OF THE NAMES, ON HOVER. The column is 120px and a task can
                          hold four people, so it shows the first and a count — and the count
                          is useless if there is no way to find out who it stands for.
                          
                          `Tip` rather than the browser's own `title`: that takes about a second
                          to appear and reads like a system dialog from another decade, which on
                          a row you are scanning is the difference between an answer and a
                          delay.
                        */
                        <Tip
                          className="w-full min-w-0"
                          label={`${row.assignees.map((p) => p.name).join(', ')} · open the task to change who is on it`}
                        >
                          {cell}
                        </Tip>
                      ) : (
                        cell
                      )}
                    </Cell>
                  )
                }
                if (column.key === 'start' || column.key === 'end') {
                  const isStart = column.key === 'start'
                  return (
                    <DateCell
                      key={column.key}
                      width={column.width}
                      value={isStart ? row.start : row.end}
                      // Neither end of a phase is typed: both come from the tasks under
                      // it. The finish was already read-only; leaving the start editable
                      // offered a field that the next roll-up would silently discard.
                      writable={mayEdit && !row.has_children}
                      active={editing?.key === row.key && editing.field === column.key}
                      onStart={() => onEdit({ key: row.key, field: column.key })}
                      onCommit={(v) => onCommit(row.key, { [isStart ? 'start_on' : 'end_on']: v })}
                    />
                  )
                }
                if (column.key === 'days') {
                  return (
                    <Cell
                      key={column.key}
                      width={column.width}
                      // The hours those days come to, for anyone who costs by the hour.
                      // The column is too narrow to say both, so it shows the days and
                      // says the hours on the hover — and takes either when you type.
                      title={
                        row.working_days
                          ? `${formatLength(row.working_days, perDay)} — ${lengthHint(perDay)}`
                          : lengthHint(perDay)
                      }
                    >
                      <Editable
                        value={row.working_days ? `${row.working_days}d` : ''}
                        active={editing?.key === row.key && editing.field === 'days'}
                        writable={mayEdit && !row.has_children}
                        onStart={() => onEdit({ key: row.key, field: 'days' })}
                        // Days or hours, whichever was typed: 4d, 32h, or a bare 4.
                        // Anything that is not a length puts the cell back untouched,
                        // rather than quietly making the task one day long.
                        onCommit={(v) => {
                          const length = parseLength(v)
                          if (length) onCommit(row.key, length)
                          else onEdit(null)
                        }}
                        onCancel={() => onEdit(null)}
                      />
                    </Cell>
                  )
                }
                if (column.key === 'progress') {
                  /**
                   * READ-ONLY. How far along a task is comes from its stage now — open the task
                   * and say whether it is in research, design, developing or testing, and this
                   * follows. It used to be a number you typed here, which meant two answers to
                   * one question and asked for a figure nobody measures.
                   */
                  return (
                    <Cell key={column.key} width={column.width}>
                      <span
                        className="tabular-nums text-[var(--color-ink-dim)]"
                        title="From the task's stage — open the task to change it"
                      >
                        {row.progress_pct === null ? '—' : `${Math.round(row.progress_pct)}%`}
                      </span>
                    </Cell>
                  )
                }
                if (column.key === 'milestone') {
                  /**
                   * SHOWN, NOT SET. A milestone was a second way of saying "this one matters",
                   * alongside the status column that already says where work has got to —
                   * two concepts nobody kept in step. Existing ones still draw; nothing new is
                   * marked one here.
                   */
                  return (
                    <Cell key={column.key} width={column.width}>
                      {row.is_milestone ? (
                        <span title="A milestone" aria-label={`${row.key} is a milestone`}>
                          ◆
                        </span>
                      ) : (
                        <span className="text-[var(--color-ink-faint)]">—</span>
                      )}
                    </Cell>
                  )
                }
                if (column.key === 'done') {
                  return (
                    <Cell key={column.key} width={column.width}>
                      <input
                        type="checkbox"
                        aria-label={`${row.key} is done`}
                        checked={row.status_category === 'done'}
                        // A phase is not tickable: ticking it off would be ticking off nine
                        // tasks at once. And ticking IS moving it to Done, so it asks
                        // `work.move` — which is a customer's at `none`.
                        disabled={!row.may_move || row.has_children}
                        className="accent-[var(--color-accent)]"
                        onChange={(e) => onTick(row.key, e.target.checked)}
                      />
                    </Cell>
                  )
                }
                if (column.key === 'waits') {
                  return (
                    <Cell key={column.key} width={column.width}>
                      <PredecessorCell
                        projectId={projectId}
                        row={row}
                        rows={rows}
                        writable={mayEdit}
                        onLink={onLink}
                        onUnlink={onUnlink}
                      />
                    </Cell>
                  )
                }
                if (column.key === 'feeds') {
                  // Read-only on purpose: a link is edited from the end that waits, which is
                  // where it is a decision. Offering it at both ends means two cells that
                  // change the same thing and disagree about which one you last touched.
                  const links = row.successors ?? []
                  return (
                    <Cell key={column.key} width={column.width}>
                      {links.length > 0 && (
                        <button
                          type="button"
                          className="w-full truncate text-left font-mono text-[11px] text-[var(--color-ink-dim)]"
                          onClick={() => onOpen(row.key)}
                          title={links.map((p) => `${p.key} (${p.relation})`).join(', ')}
                        >
                          {links.map((p) => p.key).join(', ')}
                        </button>
                      )}
                    </Cell>
                  )
                }
                return (
                  <Cell key={column.key} width={column.width}>
                    <ReadCell column={column.key} row={row} />
                  </Cell>
                )
            }
            return (
              <>
                <span
                  className="sticky left-0 z-[2] flex items-center shadow-[2px_0_5px_-3px_rgba(0,0,0,0.35)]"
                  style={{ background: rowBackground(i, row.key, selected, picked, hovered) }}
                >
                {/* The row number: its place in the whole plan, not on this screen, so it does
                    not change when a phase is folded or a filter is typed. It is what people
                    say out loud — "row 14" — and what a printout has down its edge. */}
                <span
                  className={clsx(
                    'flex shrink-0 items-center justify-center font-mono text-[10px] tabular-nums',
                    selected === row.key ? 'text-[var(--color-accent-ink)]' : 'text-[var(--color-ink-faint)]',
                  )}
                  style={{ width: NUMBER_W }}
                >
                  {row.order + 1}
                </span>
                {/* The grip, the way every outline that can be reordered by hand has one.
                    A whole row that drags would fight every cell you are trying to edit. */}
                <span
                  aria-label={`Drag to move ${row.title}`}
                  className={clsx(
                    'flex w-5 shrink-0 cursor-grab touch-none justify-center text-[11px] leading-none text-[var(--color-ink-faint)] select-none',
                    !mayArrange && 'invisible',
                    rowDrag?.key === row.key && 'cursor-grabbing text-[var(--color-accent)]',
                  )}
                  onPointerDown={(e) => {
                    if (!mayArrange) return
                    e.preventDefault()
                    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
                    onRowDragStart(row.key, e)
                  }}
                >
                  ⠿
                </span>
                  {pinnedColumns.map(cellFor)}
                </span>
                {scrollingColumns.map(cellFor)}
              </>
            )
          })()}
        </div>
      ))}

        {/* Where it would land. A drag with no indicator is a guess. */}
        {rowDrag && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 h-0.5"
            style={{ top: rowDrag.gap * ROW_H - 1, background: 'var(--color-accent)' }}
          />
        )}
      </div>
    </div>
  )
}

/**
 * The columns that are only ever read.
 *
 * They are read-only on purpose. Every one of them is either derived (the outline
 * number, the slack, the rolled-up cost) or has a proper editor of its own — the rule
 * and the deadline belong in the "when it can happen" panel, where the whole set can
 * be seen together and a constraint cannot be half-set. A cell that accepts a
 * constraint but not the date it needs is a cell that saves nonsense.
 */
function ReadCell({ column, row }: { column: string; row: Row }) {
  const dim = (text: React.ReactNode) => (
    <span className="truncate text-[var(--color-ink-dim)]">{text}</span>
  )
  const number = (text: React.ReactNode) => (
    <span className="block truncate text-right tabular-nums">{text}</span>
  )

  switch (column) {
    case 'wbs':
      return <span className="font-mono text-[11px] text-[var(--color-ink-dim)]">{row.wbs}</span>
    case 'rule':
      // Only worth a row of grid when it is not the default. "As soon as possible" on
      // every line is noise that hides the two tasks somebody actually pinned.
      if (row.constraint_type === 'asap') return null
      return (
        <span className="truncate" title={`${row.constraint_label}${row.constraint_on ? ` ${shortDate(row.constraint_on)}` : ''}`}>
          {row.constraint_label}
          {row.constraint_on && (
            <span className="ml-1 text-[var(--color-ink-dim)]">{shortDate(row.constraint_on)}</span>
          )}
        </span>
      )
    case 'deadline':
      if (!row.deadline_on) return null
      return (
        <span className={clsx('truncate', (row.past_deadline_by ?? 0) > 0 && 'text-[var(--color-danger)]')}>
          {shortDate(row.deadline_on)}
          {(row.past_deadline_by ?? 0) > 0 && ` · ${row.past_deadline_by}d late`}
        </span>
      )
    case 'slack':
    case 'free_slack': {
      const days = column === 'slack' ? row.slack_days : row.free_slack_days
      if (days === null || days === undefined) return null
      return number(<span className={clsx(days <= 0 && 'text-[var(--gantt-critical)]')}>{days}</span>)
    }
    case 'type':
      return dim(TASK_TYPE_LABELS[row.task_type] ?? row.task_type)
    case 'priority':
      return dim(row.priority ? row.priority[0]!.toUpperCase() + row.priority.slice(1) : '')
    case 'work':
      return number(hoursOf(row.estimate_s))
    case 'spent':
      return number(hoursOf(row.spent_s))
    case 'remaining':
      return number(hoursOf(row.remaining_s))
    case 'actual_cost':
      return number(money(row.actual_cost))
    case 'baseline_start':
    case 'baseline_end': {
      const agreed = column === 'baseline_start' ? row.baseline_start : row.baseline_end
      const now = column === 'baseline_start' ? row.start : row.end
      if (!agreed) return null
      // Red when the plan has drifted from what was agreed. That drift is the whole
      // reason somebody turns this column on.
      return (
        <span className={clsx('truncate', now && now !== agreed && 'text-[var(--color-danger)]')}>
          {shortDate(agreed)}
        </span>
      )
    }
    case 'cost':
      // Blank, never zero. A task with no rate behind it has an unknown cost, and
      // showing that as R 0.00 quietly understates a total somebody is going to quote.
      return number(money(row.planned_cost))
    case 'status':
      return dim(row.status_name)
    default:
      return null
  }
}

const TASK_TYPE_LABELS: Record<string, string> = {
  fixed_units: 'Fixed units',
  fixed_duration: 'Fixed duration',
  fixed_work: 'Fixed work',
}

const hoursOf = (seconds: number | null | undefined) =>
  seconds ? `${(seconds / 3600).toFixed(1)}h` : ''

const money = (value: string | null) =>
  value === null
    ? ''
    : `R ${Number(value).toLocaleString('en-ZA', { maximumFractionDigits: 0 })}`

function Cell({ width, title, children }: { width: number; title?: string; children: React.ReactNode }) {
  return (
    <span className="min-w-0 truncate px-2" style={{ width }} title={title}>
      {children}
    </span>
  )
}

/** A cell you click into and type. Escape puts it back, Enter commits. */
function Editable({
  value,
  active,
  writable,
  bold,
  numeric,
  onStart,
  onCommit,
  onCancel,
  onEnter,
}: {
  value: string
  active: boolean
  writable: boolean
  bold?: boolean
  numeric?: boolean
  onStart: () => void
  onCommit: (value: string) => void
  onCancel: () => void
  onEnter?: () => void
}) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value, active])

  if (!active) {
    return (
      <button
        type="button"
        disabled={!writable}
        onClick={onStart}
        className={clsx(
          'w-full truncate text-left disabled:cursor-default',
          bold && 'font-semibold',
          !value && 'text-[var(--color-ink-faint)]',
        )}
      >
        {value || '—'}
      </button>
    )
  }
  return (
    <input
      ref={selectOnFocus}
      value={draft}
      inputMode={numeric ? 'numeric' : undefined}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => (draft === value ? onCancel() : onCommit(draft))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          if (draft !== value) onCommit(draft)
          else onCancel()
          onEnter?.()
        }
        if (e.key === 'Escape') onCancel()
      }}
      className="w-full rounded-[3px] border border-[var(--color-accent)] bg-[var(--color-surface)] px-1 py-0.5 text-[12px] outline-none"
    />
  )
}

function DateCell({
  width,
  value,
  writable,
  active,
  onStart,
  onCommit,
}: {
  width: number
  value?: string
  writable: boolean
  active: boolean
  onStart: () => void
  onCommit: (value: string) => void
}) {
  return (
    <Cell width={width}>
      {active ? (
        <input
          ref={selectOnFocus}
          type="date"
          defaultValue={value ?? ''}
          onChange={(e) => e.target.value && onCommit(e.target.value)}
          className="w-full rounded-[3px] border border-[var(--color-accent)] bg-[var(--color-surface)] px-1 py-0.5 text-[11px] outline-none"
        />
      ) : (
        <button
          type="button"
          disabled={!writable}
          onClick={onStart}
          className={clsx(
            'w-full truncate text-left disabled:cursor-default',
            !value && 'text-[var(--color-ink-faint)]',
          )}
        >
          {value ? shortDate(value) : '—'}
        </button>
      )}
    </Cell>
  )
}

/* ------------------------------------------------------------------------ chart */

function Axis({
  from,
  to,
  width,
  dayWidth,
  height,
}: {
  from: Date
  to: Date
  width: number
  dayWidth: number
  height: number
}) {
  const end = to > addDays(from, width / dayWidth) ? to : addDays(from, width / dayWidth)
  const cells: Date[] = []
  if (dayWidth >= 12) {
    const cursor = new Date(from)
    cursor.setDate(cursor.getDate() - ((cursor.getDay() + 6) % 7))
    while (cursor <= end) {
      cells.push(new Date(cursor))
      cursor.setDate(cursor.getDate() + 7)
    }
  } else {
    const cursor = new Date(from.getFullYear(), from.getMonth(), 1)
    while (cursor <= end) {
      cells.push(new Date(cursor))
      cursor.setMonth(cursor.getMonth() + 1)
    }
  }
  const fits = dayWidth >= 12 ? 42 : 26

  return (
    <div
      className="relative border-b-2 border-[var(--gantt-axis-rule)] bg-[var(--color-surface)]"
      style={{ height, width }}
    >
      {cells.map((start, i) => {
        const next = cells[i + 1] ?? end
        const left = daysBetween(from, start) * dayWidth
        const cellWidth = Math.min(daysBetween(start, next) * dayWidth, width - left)
        if (cellWidth <= 0) return null
        const [top, bottom] =
          dayWidth >= 12
            ? [
                `${start.toLocaleDateString(undefined, { month: 'short' })} ${start.getDate()}`,
                '',
              ]
            : [
                start.toLocaleDateString(undefined, { month: 'short' }),
                i === 0 || start.getMonth() === 0 ? String(start.getFullYear()) : '',
              ]
        return (
          <div
            key={start.toISOString()}
            className={clsx(
              'absolute top-0 flex flex-col items-center justify-center overflow-hidden',
              i % 2 === 0 && 'bg-[var(--gantt-axis-band)]',
            )}
            style={{
              left,
              width: cellWidth,
              height,
              paddingTop: TODAY_LANE,
            }}
          >
            {cellWidth >= fits && (
              <>
                <span className="text-[10px] font-semibold whitespace-nowrap text-[var(--color-ink-dim)]">
                  {top}
                </span>
                {bottom && (
                  <span className="text-[9px] whitespace-nowrap text-[var(--color-ink-faint)]">
                    {bottom}
                  </span>
                )}
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** Zebra rows, and shaded weekends so a bar's length reads at a glance. */
function Background({
  rows,
  from,
  width,
  dayWidth,
  holidays,
  todayX,
}: {
  rows: number
  from: Date
  width: number
  dayWidth: number
  /** Weekdays nobody works, as ISO dates. Hatched, so a bar that skips one reads as a
   *  holiday rather than a bug. */
  holidays: string[]
  todayX: number
}) {
  const bands: number[] = []
  if (dayWidth >= 8) {
    for (let d = 0; d * dayWidth < width; d++) {
      if (isWeekend(addDays(from, d))) bands.push(d)
    }
  }
  const closed = dayWidth >= 4 ? holidays.map((iso) => daysBetween(from, parse(iso))).filter((d) => d >= 0 && d * dayWidth < width) : []
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden>
      {/* Today, as a band as well as a line: the line says where, the band says which
          day, and at a month's zoom the two are five pixels apart. */}
      <div
        className="absolute top-0"
        style={{
          left: todayX,
          width: Math.max(2, dayWidth),
          height: rows * ROW_H,
          background: 'color-mix(in srgb, var(--gantt-today) 12%, transparent)',
        }}
      />
      {closed.map((d) => (
        <div
          key={`h${d}`}
          title="Public holiday"
          className="absolute top-0"
          style={{
            left: d * dayWidth,
            width: dayWidth,
            height: rows * ROW_H,
            backgroundImage:
              'repeating-linear-gradient(135deg, color-mix(in srgb, var(--color-ink) 12%, transparent) 0 2px, transparent 2px 6px)',
          }}
        />
      ))}
      {Array.from({ length: rows }, (_, i) =>
        i % 2 === 1 ? (
          <div
            key={`z${i}`}
            className="absolute inset-x-0 bg-[var(--gantt-zebra)]"
            style={{ top: i * ROW_H, height: ROW_H }}
          />
        ) : null,
      )}
      {bands.map((d) => (
        <div
          key={`w${d}`}
          className="absolute top-0 bg-[var(--color-ink)]/[0.05]"
          style={{ left: d * dayWidth, width: dayWidth, height: rows * ROW_H }}
        />
      ))}
    </div>
  )
}

/**
 * One task on the chart, and every gesture you can make on it.
 *
 * Five handles, all of which appear on hover so a chart at rest stays a chart: the body
 * moves the task, each edge resizes it, a marker under the bar sets how far along it is,
 * and a round handle off the right-hand end draws a dependency onto another task.
 *
 * The bar sits above the dependency overlay on purpose. It did not, once, and the
 * consequence was that none of these worked — the handles rendered, the cursor changed,
 * and every pointer event landed on a transparent SVG covering the whole chart.
 */
function Bar({
  row,
  phase,
  top,
  x,
  dayWidth,
  drag,
  writable,
  selected,
  showCritical,
  onSelect,
  onDragStart,
  onPeek,
  onContext,
}: {
  row: Row
  /** The colour of the phase this task belongs to. */
  phase: string
  top: number
  x: (iso: string) => number
  dayWidth: number
  drag: Drag | null
  writable: boolean
  selected: boolean
  showCritical: boolean
  onSelect: (e: React.MouseEvent) => void
  onDragStart: (kind: Grab, e: React.PointerEvent) => void
  /** The pointer is over the bar (with its position, in chart coordinates), or has left. */
  onPeek: (at: { x: number; y: number } | null) => void
  /** A right-click on the bar, in screen coordinates. */
  onContext: (x: number, y: number) => void
}) {
  const perDay = useDayHours()
  if (!row.start || !row.end) {
    return (
      <div
        data-row-key={row.key}
        className="absolute inset-x-0 z-[3] flex items-center px-3 text-[11px] text-[var(--color-ink-faint)]"
        style={{ top, height: ROW_H }}
      >
        no dates yet — give it a start and a length
      </div>
    )
  }

  // Drawn where the pointer is. The server has not been asked yet, and will not be
  // until the drag ends — a chart that round-tripped every pixel would be unusable.
  const mine = drag?.key === row.key && drag.live
  const shifted = mine && drag.kind !== 'link' ? drag.days : 0
  const startShift = mine && drag?.kind === 'start' ? shifted : 0
  const endShift = mine && drag?.kind === 'end' ? shifted : 0
  const moveShift = mine && drag?.kind === 'move' ? shifted : 0

  const left = x(row.start) + (moveShift + startShift) * dayWidth
  const right = x(row.end) + dayWidth + (moveShift + endShift) * dayWidth
  const width = Math.max(dayWidth, right - left)
  const colour = barColour(row, showCritical, phase)
  const done = row.progress_pct ?? 0
  const dragging = Boolean(mine)
  /**
   * DRAGGING A BAR IS CHANGING THE PLAN, so it asks what typing a date asks.
   *
   * The chart took the screen-wide `writable` while the grid beside it now asks per row, and a
   * bar you can drag on a task you cannot save is the worse half of the two: the dates move
   * under the pointer and snap back when the API refuses.
   */
  const mayEdit = writable && row.editable

  if (row.milestone) {
    return (
      <div
        data-row-key={row.key}
        className={clsx('absolute z-[3]', mayEdit && 'cursor-grab')}
        style={{ top: top + (ROW_H - 14) / 2, left: left - 7, height: 14, width: 14 }}
        onPointerDown={(e) => mayEdit && onDragStart('move', e)}
        onClick={onSelect}
        onContextMenu={(e) => {
          e.preventDefault()
          onContext(e.clientX, e.clientY)
        }}
        title={`${row.key} · ${row.title}`}
      >
        <MilestoneDiamond
          className={clsx(selected && 'ring-2 ring-[var(--color-accent)]')}
        />
        <span className="pointer-events-none absolute top-1/2 left-full ml-2 -translate-y-1/2 text-[10px] whitespace-nowrap text-[var(--color-ink-dim)]">
          {shortDate(row.start)}
        </span>
      </div>
    )
  }

  const grabbable = mayEdit && !row.has_children
  // What was agreed, drawn as a thin grey bar under the live one, so a slip is a
  // picture rather than two dates in two columns.
  const agreed =
    row.baseline_start && row.baseline_end && !row.has_children
      ? { left: x(row.baseline_start), width: Math.max(2, x(row.baseline_end) + dayWidth - x(row.baseline_start)) }
      : null

  return (
    <>
      {agreed && (
        <div
          aria-hidden
          className="pointer-events-none absolute z-[2] rounded-[2px]"
          style={{
            top: top + (ROW_H - BAR_H) / 2 + BAR_H + 1,
            left: agreed.left,
            width: agreed.width,
            height: 4,
            background: 'var(--gantt-baseline, #9aa3b2)',
            opacity: 0.8,
          }}
        />
      )}
    <div
      data-row-key={row.key}
      data-bar={row.key}
      className={clsx(
        'group absolute z-[3] touch-none',
        grabbable && (dragging ? 'cursor-grabbing' : 'cursor-grab'),
      )}
      style={{ top: top + (ROW_H - BAR_H) / 2, left, width, height: BAR_H }}
      onClick={onSelect}
      onContextMenu={(e) => {
        e.preventDefault()
        onContext(e.clientX, e.clientY)
      }}
      onPointerEnter={(e) => {
        const chart = (e.currentTarget as HTMLElement).closest('[data-chart]')?.getBoundingClientRect()
        onPeek({ x: e.clientX - (chart?.left ?? 0), y: top })
      }}
      onPointerLeave={() => onPeek(null)}
      onPointerDown={(e) => {
        if (!grabbable) return
        onPeek(null)
        onDragStart('move', e)
      }}
    >
      {row.has_children ? (
        // A summary is a bracket, not a bar: a thin beam with a tooth at each end, the
        // shape every planner since Project has used so a phase never reads as a task.
        <SummaryBracket
          colour={colour}
          className={clsx(selected && 'ring-2 ring-[var(--color-accent)] ring-offset-1')}
        />
      ) : (
        <TaskFill
          colour={colour}
          done={done}
          className={clsx(
            selected && 'ring-2 ring-[var(--color-accent)] ring-offset-1',
            dragging && 'opacity-90 shadow-lg',
          )}
        />
      )}

      <span className="pointer-events-none absolute top-1/2 left-full ml-1.5 -translate-y-1/2 text-[10px] whitespace-nowrap text-[var(--color-ink-dim)]">
        {row.has_children ? (
          <span className="font-semibold" style={{ color: colour }}>{row.title}</span>
        ) : (
          <>
            {formatLength(row.working_days, perDay)}
            {done > 0 && ` · ${Math.round(done)}%`}
            {row.assignee && <span className="text-[var(--color-ink-faint)]"> · {row.assignee}</span>}
          </>
        )}
      </span>

      {grabbable && (
        <>
          <span
            aria-label="Drag to change when it starts"
            className="absolute inset-y-0 -left-0.5 w-2 cursor-ew-resize touch-none rounded-l-[3px] opacity-0 group-hover:opacity-100"
            style={{ background: 'var(--color-accent)' }}
            onPointerDown={(e) => {
              e.stopPropagation()
              onDragStart('start', e)
            }}
          />
          <span
            aria-label="Drag to change when it finishes"
            className="absolute inset-y-0 -right-0.5 w-2 cursor-ew-resize touch-none rounded-r-[3px] opacity-0 group-hover:opacity-100"
            style={{ background: 'var(--color-accent)' }}
            onPointerDown={(e) => {
              e.stopPropagation()
              onDragStart('end', e)
            }}
          />
          {/* Under the bar rather than on it, so it cannot be confused with a resize.
              The same place every other Gantt puts it. */}
          <span
            aria-label="Drag onto another task to say it waits for this one"
            title="Drag onto another task to say it waits for this one"
            className="absolute top-1/2 -right-3.5 size-3 -translate-y-1/2 cursor-crosshair touch-none rounded-full border-2 border-[var(--color-surface)] opacity-0 group-hover:opacity-100"
            style={{ background: 'var(--color-accent)' }}
            onPointerDown={(e) => {
              e.stopPropagation()
              onDragStart('link', e)
            }}
          />
        </>
      )}
    </div>
    </>
  )
}

/** The line that follows the pointer while a dependency is being drawn. */
function LinkGhost({
  drag,
  rows,
  x,
}: {
  drag: Drag
  rows: Row[]
  x: (iso: string) => number
}) {
  const index = rows.findIndex((r) => r.key === drag.key)
  const row = rows[index]
  if (!row?.end) return null
  const fromX = x(row.end)
  const fromY = index * ROW_H + ROW_H / 2
  return (
    <svg className="pointer-events-none absolute inset-0 z-[6] size-full overflow-visible">
      <line
        x1={fromX}
        y1={fromY}
        x2={drag.x}
        y2={drag.y - (TODAY_LANE + LABEL_LANE)}
        stroke="var(--color-accent)"
        strokeWidth={2}
        strokeDasharray="4 3"
      />
    </svg>
  )
}

function Dependencies({
  rows,
  links,
  x,
  dayWidth,
  width,
  onRemove,
}: {
  rows: Row[]
  links: Dependency[]
  x: (iso: string) => number
  dayWidth: number
  width: number
  onRemove: (from: string, to: string) => void
}) {
  const index = new Map(rows.map((r, i) => [r.id, i]))
  return (
    // pointer-events-none on the root, and back on only for the fat click targets.
    // Without this the overlay sits on top of every bar and swallows the pointer, so
    // nothing on the chart can be dragged at all — which is exactly what happened: the
    // move, resize and link handles were all there and all unreachable.
    <svg
      className="pointer-events-none absolute inset-0 z-[2] overflow-visible"
      width={width}
      height={rows.length * ROW_H}
    >
      <defs>
        {/* refX is the marker's own tip, so the point lands exactly on the end of the
            path — which is a few pixels clear of the bar, not on it. */}
        <marker id="sched-arrow" markerWidth="7" markerHeight="7" refX="7" refY="3.5" orient="auto">
          <path d="M0,0 L7,3.5 L0,7 z" fill="var(--gantt-link)" />
        </marker>
      </defs>
      {links.map((linkRow) => {
        const a = index.get(linkRow.from)
        const b = index.get(linkRow.to)
        if (a === undefined || b === undefined) return null
        const from = rows[a]!
        const to = rows[b]!
        if (!from.start || !from.end || !to.start || !to.end) return null

        const ends = anchors(linkRow.relation)
        // The bar spans from the left of its first day to the right of its last, so the
        // right-hand edge is one whole day past the last day's cell. It was a hard-coded
        // six pixels before, which is roughly right at one zoom level and starts the
        // line somewhere inside the bar at every other one.
        const edge = (row: Row, side: 'start' | 'end') =>
          side === 'end' ? x(row.end!) + dayWidth : x(row.start!)

        const d = route(
          edge(from, ends.from),
          a * ROW_H + ROW_H / 2,
          edge(to, ends.to),
          b * ROW_H + ROW_H / 2,
          ends.from,
          ends.to,
          ROW_H,
        )
        const label = linkRow.relation === 'FS' ? '' : ` (${linkRow.relation})`

        return (
          <g key={`${linkRow.from}-${linkRow.to}`}>
            <path
              d={d}
              fill="none"
              stroke="var(--gantt-link)"
              strokeWidth={1.5}
              markerEnd="url(#sched-arrow)"
            />
            {/* A fat invisible line to click, because a one-pixel path is not a target. */}
            <path
              d={d}
              fill="none"
              stroke="transparent"
              strokeWidth={10}
              className="pointer-events-auto cursor-pointer"
              onClick={() => onRemove(from.key, to.key)}
            >
              <title>{`${from.key} → ${to.key}${label}. Click to remove.`}</title>
            </path>
          </g>
        )
      })}
    </svg>
  )
}

function TodayMarker({ left, height }: { left: number; height: number }) {
  const centred = left >= 26
  return (
    <div aria-hidden className="pointer-events-none absolute top-0 z-[2]" style={{ left, height }}>
      <span
        className="absolute top-0 block text-[9px] leading-[12px] font-bold tracking-wider whitespace-nowrap uppercase"
        style={{
          color: 'var(--gantt-today)',
          left: centred ? 0 : 1,
          transform: centred ? 'translateX(-50%)' : 'none',
        }}
      >
        Today
      </span>
      <div
        className="absolute w-0 border-l-2"
        style={{ top: TODAY_LANE, height: height - TODAY_LANE, borderColor: 'var(--gantt-today)' }}
      />
    </div>
  )
}

function Legend({ showCritical, hasBaseline }: { showCritical: boolean; hasBaseline: boolean }) {
  return (
    <MarksLegend
      extra={[
        [showCritical ? 'Critical path' : 'Late', 'var(--gantt-critical)'],
        ['Milestone', 'var(--gantt-milestone)'],
        ...(hasBaseline
          ? ([['What was agreed at baseline', 'var(--gantt-baseline, #9aa3b2)']] as [string, string][])
          : []),
      ]}
      trailing={
        <span className="ml-auto">
          Drag a bar to move it · drag its edges to change how long it takes · drag the
          round handle onto another task to say it waits for this one
        </span>
      }
    />
  )
}


/**
 * What the drag is about to do, followed under the pointer.
 *
 * Added after a stray drag on the progress handle set four live tasks to nought per cent.
 * Nothing on screen said what was being changed until it had been, and the only evidence
 * afterwards was the number itself. A drag that shows its own result is one you can abandon
 * before you let go — which is worth keeping for the drags that remain, now that the progress
 * handle it was written for has gone.
 */
function DragReadout({
  drag,
  rows,
  x,
  dayWidth,
}: {
  drag: Drag
  rows: Row[]
  x: (iso: string) => number
  dayWidth: number
}) {
  const index = rows.findIndex((r) => r.key === drag.key)
  const row = rows[index]
  if (!row || !row.start || !row.end) return null

  let says: string
  {
    const next = resultOf(drag, row)
    if (!next) return null
    says =
      drag.kind === 'move'
        ? `${shortDate(next.start)} – ${shortDate(next.end)}`
        : drag.kind === 'start'
          ? `starts ${shortDate(next.start)}`
          : `finishes ${shortDate(next.end)}`
  }

  const shift = drag.days * dayWidth
  return (
    <div
      aria-live="polite"
      className="pointer-events-none absolute z-[7] -translate-x-1/2 rounded-[var(--radius-control)] border border-[var(--color-rule-strong)] bg-[var(--color-surface-raised)] px-2 py-1 text-[11px] font-medium whitespace-nowrap shadow-lg"
      style={{
        left: x(row.start) + shift + 20,
        top: index * ROW_H - 26,
      }}
    >
      {says}
      {drag.days !== 0 && (
        <span className="ml-1.5 text-[var(--color-ink-dim)]">
          {drag.days > 0 ? `+${drag.days}` : drag.days} days
        </span>
      )}
    </div>
  )
}


/**
 * Getting a plan in and out as Microsoft Project XML.
 *
 * The one format every planning tool reads, so it is the door in for a plan that already
 * exists in MS Project, ProjectLibre or Smartsheet — and the door out for anybody who
 * wants to leave. A product that can only be typed into is a product people are wary of
 * committing a real project to.
 *
 * Importing is additive and says what it could not bring across. It never creates people:
 * inventing user accounts from a file is inventing people with access to a client's
 * project.
 */
function Interchange({
  projectId,
  writable,
}: {
  projectId: string
  writable: boolean
}) {
  const qc = useQueryClient()
  const toast = useToast()
  const input = useRef<HTMLInputElement>(null)
  const [result, setResult] = useState<{
    tasks: number
    links: number
    assignments: number
    people_not_found: string[]
    ignored: string[]
    from_file: string
  } | null>(null)

  const bring = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData()
      body.append('file', file)
      return api.upload<{
        tasks: number
        links: number
        assignments: number
        people_not_found: string[]
        ignored: string[]
        from_file: string
      }>(`/projects/${projectId}/plan.xml`, body)
    },
    onSuccess: (out) => {
      setResult(out)
      qc.invalidateQueries({ queryKey: ['plan', projectId] })
      toast(`Brought in ${out.tasks} tasks from ${out.from_file}.`, 'success')
    },
    onError: (e: Error) => toast(e.message, 'danger'),
  })

  return (
    <>
      <Tool
        label="Export as Microsoft Project XML — readable by Project, ProjectLibre and Smartsheet"
        /* `/api`, not the kit's own `/api/v1`. Opened in a tab rather than fetched, because
           the session is a cookie the browser sends on its own — see lib/api.ts. */
        onClick={() => window.open(`/api/projects/${projectId}/plan.xml`, '_blank')}
      >
        <ExportIcon />
      </Tool>
      {writable && (
        <>
          <Tool label="Import a Microsoft Project XML file" disabled={bring.isPending} onClick={() => input.current?.click()}>
            <ImportIcon />
          </Tool>
          <input
            ref={input}
            type="file"
            accept=".xml,application/xml,text/xml"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) bring.mutate(file)
              e.target.value = ''
            }}
          />
        </>
      )}

      {result && (
        <Dialog
          open
          onClose={() => setResult(null)}
          title={`Brought in from ${result.from_file}`}
          footer={<Button variant="primary" onClick={() => setResult(null)}>Right</Button>}
        >
          <div className="flex flex-col gap-2 text-[13px]">
            <p>
              {result.tasks} {result.tasks === 1 ? 'task' : 'tasks'}, {result.links}{' '}
              {result.links === 1 ? 'dependency' : 'dependencies'} and {result.assignments}{' '}
              {result.assignments === 1 ? 'assignment' : 'assignments'}. Nothing that was
              already here was touched.
            </p>
            {result.people_not_found.length > 0 && (
              <div>
                <p className="font-medium">Nobody here matches these names, so their work is unassigned:</p>
                <p className="text-[var(--color-ink-dim)]">
                  {result.people_not_found.join(', ')}
                </p>
                <p className="mt-1 text-[11px] text-[var(--color-ink-faint)]">
                  An import never creates accounts — that would be creating people with
                  access to a client's project.
                </p>
              </div>
            )}
            {result.ignored.map((note) => (
              <p key={note} className="text-[12px] text-[var(--color-ink-dim)]">
                {note}
              </p>
            ))}
          </div>
        </Dialog>
      )}
    </>
  )
}
